/**
 * Hello Retail JS map
 * 1) Module toggles and shared tracking/cookie helpers
 * 2) Featured collection custom element
 * 3) Search drawer and typewriter custom elements
 * 4) Predictive search custom element (search page + drawer)
 * 5) Shared filter/range/url helper functions
 * 6) Range filter custom element
 * 7) Collection page custom element (facets, sorting, pagination)
 */
/**
 * Toggle Hello Retail modules on/off.
 * Optional runtime override: set window.HELLO_RETAIL_COMPONENTS before this script loads.
 */
const HELLO_RETAIL_COMPONENTS = {
  trackingUser: true,
  featuredCollection: true,
  predictiveSearch: true,
  rangeFilter: true,
  collectionPage: true,
  ...(window.HELLO_RETAIL_COMPONENTS || {}),
};

/**
 * Read a cookie value by name.
 * Returns null when the cookie does not exist.
 */
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

/**
 * Convenience getter for the Hello Retail tracking cookie.
 */
const getTrackingIdCookie = () => getCookie('hello_retail_id');

/**
 * Persist the Hello Retail tracking ID in a cookie.
 */
const setTrackingIdCookie = (trackingId) => {
  document.cookie = `hello_retail_id=${trackingId}; max-age=3600; path=/`;
};

/**
 * Fetch a tracking user from Hello Retail.
 * Returns a normalized result object for easier caller handling.
 */
const getTrackingId = async () => {
  try {
    const response = await fetch('https://core.helloretail.com/serve/trackingUser', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      return { success: false, status: response.status, message: response.statusText };
    }

    const json = await response.json();
    return { success: true, data: json, message: 'Loaded tracking user successfully' };
  } catch (error) {
    return { success: false, status: 500, message: error.message, data: null };
  }
};

if (HELLO_RETAIL_COMPONENTS.trackingUser) {
  // Initialize tracking cookie as soon as the DOM is ready.
  document.addEventListener('DOMContentLoaded', async () => {
    try {
      if (getTrackingIdCookie()) {
      } else {
        const trackingIdResult = await getTrackingId();
        if (trackingIdResult.success) {
          setTrackingIdCookie(trackingIdResult.data.id);
        }
      }
    } catch (error) {
      console.error('Error storing Tracking Id in a cookie', error);
    }
  });
}

const getProductInformation = async (allVariantIds = []) => {
  const shopifyGraphqlUrl = `https://${Shopify.shop}/api/2026-01/graphql.json`;
  const shopifyAccessToken = '883e704e1690b79d7245bc79351d8198';
  const allVariantIdsString = allVariantIds.map(id => `"gid://shopify/ProductVariant/${id}"`).join(',');
  const countryCode = window.helloRetail.countryCode;
  const languageCode = window.helloRetail.languageCode;

  const graphql = JSON.stringify({
    query: `query getProductFromVariantId @inContext(country: ${countryCode}, language: ${languageCode}) { nodes(ids: [${allVariantIdsString}]) {
    ... on ProductVariant {
      id
      title
      hoverImage: metafield(namespace: "rootagency", key: "variant_images") {
        references(first: 2) {
          nodes {
            ... on Media {
              id
              previewImage {
                url
              }
            }
          }
        }
      }
      image {
        id
        url
      }
      availableForSale
      price {
        amount
        currencyCode
      }
      compareAtPrice {
        amount
        currencyCode
      }
      product {
        id
        title
        vendor
        options(first: 100) {
          name
          optionValues {
            id
            name
            firstSelectableVariant {
              title
              availableForSale
              image {
                url
              }
              secondaryFilterColors: metafield(namespace: "custom", key: "variant_color_fallback") {
                references(first: 1) {
                  nodes {
                    ... on Metaobject {
                      fields {
                        key
                        value
                        reference {
                          ... on MediaImage {
                            image {
                              url(transform: { maxWidth: 100 })
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
            swatch {
              color
              image {
                id
                previewImage {
                  url
                }
              }
            }
          }
        }
        badges: metafield(namespace: "rootagency", key: "labels") {
          value
          type
          references(first: 10) {
            nodes { 
              ... on Metaobject {
                id
                fields {
                  key
                  value
                }
              }
            }
          }
        }
        variants(first: 100) {
          nodes {
            title
            id
            hoverImage: metafield(namespace: "rootagency", key: "variant_images") {
              references(first: 2) {
                nodes {
                  ... on Media {
                    id
                    previewImage {
                      url
                    }
                  }
                }
              }
            }
            image {
              id
              url
            }
            availableForSale
            price {
              amount
              currencyCode
            }
            compareAtPrice {
              amount
              currencyCode
            }
          }
        }
      }
    }
    }
  }`,
    variables: {}
  })

  try {
    const response = await fetch(shopifyGraphqlUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': shopifyAccessToken,
      },
      body: graphql,
    });

    if (!response.ok) {
      return { success: false, status: response.status, message: response.statusText };
    }

    const data = await response.json();

    return { success: true, data: data?.data?.nodes, message: 'Product information loaded successfully' };
  } catch (error) {
    return { success: false, status: 500, message: error.message, data: null };
  }
  
}

if (HELLO_RETAIL_COMPONENTS.featuredCollection && !customElements.get('hello-retail-featured-collection')) {
  customElements.define(
    'hello-retail-featured-collection',
    class HelloRetail extends HTMLElement {
    async connectedCallback() {
      this.boxId = this.getAttribute('data-box-id');
      this.helloRetailSectionElement = this;
      this.sectionId = this.getAttribute('data-section-id')
      this.websiteId = window.helloRetail.hello_retail_website_uuid;
      this.helloRetailUrl = `https://core.helloretail.com/serve/recoms`;
      this.recommendationsContainer = this.querySelector('.product-grid');
      this.recommendations = null;

      this.#loadRecommendationsAndProductInformation();
    }

    async #loadRecommendationsAndProductInformation() {
      if (!this.boxId) {
        throw new Error('Box ID not found');
      }
      if (!this.websiteId) {
        throw new Error('Website ID not found');
      }

      // Set loading state on collection element
      this.setAttribute('aria-busy', 'true');

      // Load the recommendations
      try {
        const recommendationsResults = await this.#loadRecommendations();
        const products = recommendationsResults?.data?.responses?.[0]?.products ?? [];
        const allVariantIds = products.map(product => product.productNumber).filter(Boolean);

        if (allVariantIds.length === 0) {
          this.#handleEmptyRecommendations();
          return { success: true, data: [], message: 'No products found for recommendations' };
        }
        
        const productInformationResults = await getProductInformation(allVariantIds);

        // Get the product Cards from the product information results
        if (productInformationResults?.data?.length) {
          this.removeAttribute('hidden');
          this.recommendationsContainer.innerHTML = '';

          productInformationResults?.data?.forEach(product => {
            if (!product) return;
            const enhanceProductInformation = products.find(recomProduct => recomProduct.productNumber == product.id?.replace('gid://shopify/ProductVariant/', ''));
            const productCard = createProductCard(product, enhanceProductInformation, this.sectionId);

            this.recommendationsContainer.insertAdjacentHTML('beforeend', productCard);
          });

          return { success: true, data: productInformationResults.data, message: 'Product information loaded successfully' };
        }

        this.#handleEmptyRecommendations();
        return { success: true, data: [], message: 'No products found for recommendations' };
      } catch (error) {
        this.#handleEmptyRecommendations();
        throw new Error('Error loading recommendations');
      } finally {
        this.setAttribute('aria-busy', 'false');
      }

      if (!this.recommendations || !this.allProductIds) {
        throw new Error('No recommendations or product IDs found');
      }

      // Load the product information
      try {
        // const result = await this.#renderNewLiquidSection();

        this.productInformation = result.data?.data?.nodes ?? null;
      } catch (error) {
        throw new Error('Error loading product information');
      }
    }

    async #loadRecommendations() {
      const body = {
        websiteUuid: this.websiteId,
        requests: [
          {
            key: this.boxId,
            fields: ["title", "originalUrl", "trackingCode", "productNumber", "extraData"],
            context: {
              // url: window.location.href
              productNumbers: [
                window.helloRetail.productNumber
              ]
            }
          },
        ],
      };
      const tid = getTrackingIdCookie();
      if (tid) {
        body.trackingUserId = tid;
      }
      try {
        const response = await fetch(this.helloRetailUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }); 
  
        if (!response.ok) {
          return { success: false, status: response.status, message: response.statusText };
        }
  
        const json = await response.json();

        return { success: true, data: json, message: 'Recommendations loaded successfully' };
      } catch (error) {
        return { success: false, status: 500, message: error.message, data: null };
      }
    }

    #isShopifyDesignMode() {
      return Boolean(window.Shopify && window.Shopify.designMode);
    }

    #handleEmptyRecommendations() {
      if (this.#isShopifyDesignMode()) {
        this.removeAttribute('hidden');
        if (this.recommendationsContainer) {
          this.recommendationsContainer.innerHTML = '<p class="text-center">Geen producten geladen</p>';
        }
        return;
      }

      this.setAttribute('hidden', '');
    }

    disconnectedCallback() {
      console.log('disconnectedCallback', this);
    }
    },
  );
}

if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('hello-retail-search-drawer')) {
  customElements.define('hello-retail-search-drawer', class HelloRetailSearchDrawer extends HTMLElement {});
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

if (HELLO_RETAIL_COMPONENTS.predictiveSearch && !customElements.get('predictive-search')) {
  customElements.define(
    'predictive-search',
    class PredictiveSearch extends HTMLFormElement {
      /** Initialize search state, events and first fetch. */
      constructor() {
        super();

        this.focusElement = this.input;
        this.apiKey = window.helloRetail.hello_retail_api_key;
        this.websiteId = window.helloRetail.hello_retail_website_uuid;
        this.searchId = this.getAttribute('data-search-id');
        this.recomsId = this.getAttribute('data-recoms-id');
        this.helloRetailUrl = `https://core.helloretail.com/serve/search`;
        this.recomsUrl = `https://core.helloretail.com/serve/recoms`;
        this.sectionId = this.getAttribute('data-section-id');
        this.context = this.getAttribute('data-context');
        this.searchResults = this.querySelector('#PredictiveSearchResults-' + this.sectionId);
        this.oldProductGridElement = document.querySelector(`#ProductGridContainer[data-section-id="${this.sectionId}"]`);
        this.cardGridElement = this.oldProductGridElement?.querySelector('.card-grid');
        this.limit = this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit'), 10) : 3;
        this.currentPage = 1;
        this.totalPages = 1;
        this.cachedRecommendations = null;

        this.fallbackHTML = this.searchResults?.querySelector('[data-search-fallback]')?.outerHTML || null;

        this.resetButton.addEventListener('click', this.clear.bind(this));
        this.input.addEventListener('click', this.hideTypewriter.bind(this));
        this.input.addEventListener('input', theme.utils.debounce(this.onChange.bind(this), 300));

        if (!this.searchId) {
          throw new Error('Search ID not found');
        }
        if (!this.apiKey) {
          throw new Error('API Key not found');
        }

        // Drawer context: defer loading until the drawer opens.
        if (this.context !== 'drawer') {
          if (this.context === 'page' && !this.input?.value) {
            this.showRecommendations();
          } else {
            this.renderSearchResults();
          }
        }
      }

      connectedCallback() {
        if (this.context !== 'drawer') return;
        const drawer = this.closest('search-drawer');
        if (!drawer) return;
        drawer.addEventListener('drawer:afterShow', () => {
          if (!this.input?.value) this.showRecommendations();
        });
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      /** Reset button shown inside the search field. */
      get resetButton() {
        return this.querySelector('button[type="reset"]');
      }

      /** Typewriter placeholder node (if enabled in theme settings). */
      get typewriter() {
        return this.input.previousElementSibling;
      }

      /** Hide the typewriter animation after first interaction. */
      hideTypewriter() {
        if (this.typewriter && !this.typewriter.hasAttribute('hidden')) {
          this.typewriter.setAttribute('hidden', '');
        }
      }

      onFocus(event) {
        if (this.closest('.collection')) {
          document.body.classList.add('predictive-search-open');

          const searchTerm = this.getQuery();
          if (searchTerm.length === 0) return;

          this.renderSection(event);
        }
      }

      clear(event = null) {
        if (event) event.preventDefault();

        this.input.value = '';
        this.input.focus();
        this.removeAttribute('results');
        this.currentPage = 1;
        if (this.context === 'page') {
          const url = new URL(window.location.href);
          url.searchParams.delete('q');
          window.history.replaceState({}, '', url.toString());
          this.showRecommendations();
        } else if (this.context === 'drawer') {
          this.showRecommendations();
        }
      }

      getQuery() {
        if (this.input.value) {
          return this.input.value.trim();
        }

        return 'initial-search';
      }

      /** Debounced input handler: reset page and reload results. */
      async onChange() {
        if (!this.input.value.trim()) {
          this.removeAttribute('results');
          this.currentPage = 1;
          if (this.context === 'drawer') {
            this.input.value = '';
            this.showRecommendations();
          } else if (this.context === 'page') {
            const url = new URL(window.location.href);
            url.searchParams.delete('q');
            window.history.replaceState({}, '', url.toString());
            this.cachedRecommendations = null;
            this.showRecommendations();
          }
          return;
        }

        this.currentPage = 1;
        this.hideTypewriter();

        try {
          await this.renderSearchResults();
        } catch {
          this.#restoreDefaultContent();
        }
      }

      #buildSkeleton(count = 10) {
        const ratio = window.productCardInfo?.image_ratio || 'portrait';
        const items = Array.from({ length: count }, () => `
          <div class="search__skeleton-item">
            <div class="search__skeleton-image search__skeleton-image--${ratio}"></div>
            <div class="search__skeleton-content">
              <div class="search__skeleton-line search__skeleton-line--title"></div>
              <div class="search__skeleton-line search__skeleton-line--price"></div>
            </div>
          </div>
        `).join('');
        return `
          <div class="search__skeleton">
            <div class="search__skeleton-heading"></div>
            <div class="search__skeleton-list">${items}</div>
          </div>
        `;
      }

      #restoreDefaultContent() {
        if (this.fallbackHTML) {
          this.searchResults.innerHTML = this.fallbackHTML;
          this.searchResults.querySelector('[data-search-fallback]')?.removeAttribute('hidden');
        } else {
          this.searchResults.innerHTML = `
            <div class="drawer__sticky z-10 sticky left-0 bottom-0 w-full">
              <button class="button button--primary icon-with-text w-full" type="submit" is="hover-button">
                <span class="btn-fill" data-fill></span>
                <span class="btn-text">
                  ${window.translate?.general?.search?.view_more ?? 'Bekijk alle resultaten'}
                  <svg class="icon icon-arrow-right icon-sm transform" viewBox="0 0 21 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M3 10H18M18 10L12.1667 4.16675M18 10L12.1667 15.8334"></path>
                  </svg>
                </span>
              </button>
            </div>
          `;
        }
        this.removeAttribute('results');
      }

      async showRecommendations() {
        if (!this.recomsId || !this.websiteId) return;

        this.setAttribute('results', '');
        this.setAttribute('aria-busy', 'true');

        try {
          if (!this.cachedRecommendations) {
            const result = await this.#fetchRecommendations();
            if (!result.success) {
              this.#restoreDefaultContent();
              return;
            }
            this.cachedRecommendations = result.data?.responses?.[0]?.products ?? [];
          }

          const products = this.cachedRecommendations;
          if (!products.length) {
            this.#restoreDefaultContent();
            return;
          }

          const allVariantIds = products.map(p => p.productNumber).filter(Boolean);
          const productInformationResults = await getProductInformation(allVariantIds);

          if (!productInformationResults?.data?.length) {
            this.#restoreDefaultContent();
            return;
          }

          const allProductCards = productInformationResults.data.map(productInformation => {
            const enhanceProductInformation = products.find(
              p => p.productNumber == productInformation.id?.replace('gid://shopify/ProductVariant/', '')
            );
            return createProductCard(productInformation, enhanceProductInformation, this.sectionId);
          });

          if (this.context === 'page') {
            if (this.cardGridElement) {
              this.cardGridElement.innerHTML = allProductCards.join('');
            }
          } else {
            this.searchResults.innerHTML = `
              <div class="grid gap-3 md:gap-4 w-full">
                <p class="search__heading block text-xs uppercase leading-tight tracking-widest">Aanbevolen</p>
                <div class="horizontal-products grid">${allProductCards.join('')}</div>
              </div>
            `;
          }
        } catch {
          this.#restoreDefaultContent();
        } finally {
          this.removeAttribute('loading');
          this.setAttribute('aria-busy', 'false');
        }
      }

      async #fetchRecommendations() {
        const body = {
          websiteUuid: this.websiteId,
          requests: [{
            key: this.recomsId,
            fields: ['title', 'originalUrl', 'trackingCode', 'productNumber', 'extraData'],
            context: { url: window.location.href },
          }],
        };
        const tid = getTrackingIdCookie();
        if (tid) body.trackingUserId = tid;

        try {
          const response = await fetch(this.recomsUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
          if (!response.ok) return { success: false };
          const json = await response.json();
          return { success: true, data: json };
        } catch {
          return { success: false };
        }
      }

      async renderSearchResults() {
        try {
          const searchResults = await this.loadSearchResults();
          let allVariantIds = [];

          if (this.getAttribute('data-context') === 'page') {
            // Update url with the search term
            const url = new URL(window.location.href);
            url.searchParams.set('q', this.getQuery());
            window.history.replaceState({}, '', url.toString());
          }

          if (this.getQuery() === 'initial-search') {
            allVariantIds = searchResults.data.products?.initialContent?.[0]?.products?.map(product => product.productNumber);          
          } else if (searchResults.data.products?.totalCount == 0) {
            allVariantIds = searchResults.data.products?.zeroResultContent?.[0]?.products?.map(product => product.productNumber);          
          } else {
            allVariantIds = searchResults.data.products?.results?.map(product => product.productNumber);
          }

          const productInformationResults = await getProductInformation(allVariantIds);

          if (productInformationResults?.data) {
            this.searchResults.innerHTML = '';

            if (this.cardGridElement) {
              this.cardGridElement.innerHTML = '';
            }

            const allProductCards = [];

            productInformationResults?.data?.forEach(productInformation => {
              let enhanceProductInformation = null;
              if (this.getQuery() === 'initial-search') {        
                enhanceProductInformation = searchResults.data.products?.initialContent?.[0]?.products?.find(searchProduct => searchProduct.productNumber == productInformation.id?.replace('gid://shopify/ProductVariant/', ''));
              } else if (searchResults.data.products?.totalCount == 0) {
                enhanceProductInformation = searchResults.data.products?.zeroResultContent?.[0]?.products?.find(searchProduct => searchProduct.productNumber == productInformation.id?.replace('gid://shopify/ProductVariant/', ''));
              } else {
                enhanceProductInformation = searchResults.data.products?.results?.find(searchProduct => searchProduct.productNumber == productInformation.id?.replace('gid://shopify/ProductVariant/', ''));
              }

              const productCard = createProductCard(productInformation, enhanceProductInformation, this.sectionId);

              allProductCards.push(productCard);
            });

            const finalProductCards = `
              <div class="grid gap-3 md:gap-4 w-full">
                <p class="search__heading block text-xs uppercase leading-tight tracking-widest">
                  ${window.translate.general.search.products || 'Producten'}
                </p>
                <div class="horizontal-products grid">
                  ${allProductCards?.join('')}
                </div>
              </div>
            `

            if (this.cardGridElement) {
              this.cardGridElement.insertAdjacentHTML('beforeend', allProductCards?.join(''));
            }

            this.searchResults.insertAdjacentHTML('beforeend', finalProductCards);
          }
          
          if (searchResults?.data?.categories) {
            const allCategories = searchResults?.data?.categories?.results ?? [];

            if (allCategories?.length > 0) {
              const categoryCard = createCategoryCard(allCategories);
  
              this.searchResults.insertAdjacentHTML('beforeend', categoryCard);
            }
          }

          if (this.getQuery() !== 'initial-search' && this.getAttribute('data-context') !== 'page' && searchResults?.data?.products?.results?.length > 0) {
            const viewAllResultsButtonHtml = `
              <div class="drawer__sticky z-10 sticky left-0 bottom-0 w-full">
                <button class="button button--primary icon-with-text w-full" type="submit" is="hover-button">
                  <span class="btn-fill" data-fill></span>
                  <span class="btn-text">
                    ${window.translate.general.search.view_more}
                    <svg class="icon icon-arrow-right icon-sm transform" viewBox="0 0 21 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M3 10H18M18 10L12.1667 4.16675M18 10L12.1667 15.8334"></path>
                    </svg>
                  </span>
                </button>
              </div>
            `
            
            this.searchResults.insertAdjacentHTML('beforeend', viewAllResultsButtonHtml);
          }
          
          this.removeAttribute('loading');
          this.setAttribute('results', '');

          this.oldProductGridElement?.setAttribute('aria-busy', 'false');

          if (productInformationResults?.data || searchResults?.data?.categories) {
            return { success: true, data: productInformationResults.data, message: 'Product information loaded successfully' };
          }

          throw new Error('No products or collections were found.');
        } catch (error) {
          if (this.context === 'page' && this.getQuery() !== 'initial-search') {
            await this.#loadShopifyFallback();
            return;
          }
          throw new Error('Error loading search results');
        }
      }

      async #loadShopifyFallback() {
        try {
          const query = this.getQuery();
          const response = await fetch(`/search.json?q=${encodeURIComponent(query)}&type=product&limit=${this.limit}`);
          if (!response.ok) throw new Error('Shopify search failed');

          const json = await response.json();
          const variantIds = json.results
            .filter(p => p.variants?.length)
            .map(p => p.variants[0].id);

          if (variantIds.length) {
            const productInformationResults = await getProductInformation(variantIds);
            if (productInformationResults?.data?.length) {
              if (this.cardGridElement) {
                this.cardGridElement.innerHTML = '';
                const cards = productInformationResults.data.map(p => createProductCard(p, null, this.sectionId));
                this.cardGridElement.insertAdjacentHTML('beforeend', cards.join(''));
              }
            }
          }
        } catch {
          // Both HR and Shopify failed — leave existing empty state intact
        } finally {
          this.removeAttribute('loading');
          this.setAttribute('results', '');
          this.oldProductGridElement?.setAttribute('aria-busy', 'false');
        }
      }

      /** Build request payload and fetch search results from Hello Retail. */
      async loadSearchResults() {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const start = (this.currentPage - 1) * this.limit;
        const body = {
          query: this.getQuery(),
          key: this.searchId,
          products: {
            returnFilters: true,
            start,
            count: this.limit,
            fields: [
              'title',
              'price',
              'productNumber',
              'trackingCode',
              'originalUrl',
              'url',
              'extraData.handle',
            ],
            returnInitialContent: true,
          },
          categories: {
            start: 0,
            count: 3,
            fields: [
              "title",
              "originalUrl",
              "trackingCode"
            ]
          },
          format: 'json',
        };

        if (body.query !== 'initial-search') {
          const tid = getTrackingIdCookie();
          if (tid) {
            body.trackingUserId = tid;
          }
        }

        this.setAttribute('loading', '');
        this.setAttribute('aria-busy', 'true');
        this.oldProductGridElement?.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(this.helloRetailUrl, { 
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${this.apiKey}`,
            },
            body: JSON.stringify(body),
            signal: this.abortController.signal 
          })
          if (!response.ok) {
            throw new Error('Error loading search results');
          }

          const json = await response.json();

          return { success: true, data: json, message: 'Search results loaded successfully' };
        } catch (error) {
          throw new Error('Error loading search results');
        }
      }
    }, { extends: 'form' }
  )
}

/** URL query keys that are not Hello Retail facet params (pagination, sort, theme views). */
const HR_NON_FACET_URL_KEYS = new Set(['page', 'sort_by', 'view', 'section_id']);

/**
 * Hello Retail only returns date/price style sorters — its default (relevance) ordering has no
 * entry of its own, so without this synthetic option a shopper who picked a sorter could never get
 * back to it. Selecting it drops `sort_by` from the URL and sends an empty `sorting` array to HR.
 */
const HR_FEATURED_SORT_VALUE = 'featured';

/**
 * True when the URL may contain facet/filter parameters that must be validated against HR filter metadata
 * (anything other than pagination, sort, theme views, and utm_* tracking params).
 */
function rawUrlSearchParamsHasFacetLikeParams(params) {
  for (const [key, value] of params.entries()) {
    if (HR_NON_FACET_URL_KEYS.has(key)) continue;
    if (key.startsWith('utm_')) continue;
    if (value === '' || value == null) continue;
    return true;
  }
  return false;
}

/** Parse HR list facet query into the value used in URLs and checkboxes. */
const listCheckboxValueFromQuery = (query, handle) => {
  if (!query || !handle) return '';
  const prefix = `${handle}:`;
  if (query.startsWith(prefix)) return query.slice(prefix.length);
  const i = query.indexOf(':');
  return i === -1 ? query : query.slice(i + 1);
};

const normalizeUrlFacetValue = (value) => (value === '1' ? 'true' : value);

/** Decode + trim URL param values so they match HR facet option strings. */
const decodeUrlFacetParamValue = (value) => {
  if (value == null || value === '') return '';
  const raw = String(value).trim();
  try {
    return decodeURIComponent(raw.replace(/\+/g, ' ')).trim();
  } catch {
    return raw;
  }
};

/** HR sometimes omits `filter.name`; use the same fallback as param/handle in templates. */
const getHelloRetailFacetHandle = (filter) => {
  if (typeof filter?.name === 'string' && filter.name.length > 0) return filter.name;
  if (typeof filter?.settings?.name === 'string' && filter.settings.name.length > 0) {
    return filter.settings.name;
  }
  return '';
};

const buildFacetParamToFilterMap = (filterData) => {
  const map = new Map();
  for (const filter of filterData || []) {
    const type = filter?.settings?.type;
    if (type === 'RANGE') {
      map.set('filter.v.price.gte', filter);
      map.set('filter.v.price.lte', filter);
      continue;
    }
    const paramName = filter?.settings?.name;
    const filterName = filter?.name;
    if (typeof paramName === 'string' && paramName.length > 0) {
      map.set(paramName, filter);
    }
    if (typeof filterName === 'string' && filterName.length > 0 && filterName !== paramName) {
      map.set(filterName, filter);
    }
  }
  return map;
};

const collectAllowedFilterValuesFromHelloRetail = (filter) => {
  const allowed = new Set();
  const type = filter?.settings?.type;
  const handle = getHelloRetailFacetHandle(filter);
  if (!handle || (type !== 'LIST' && type !== 'BOOLEAN')) return allowed;
  for (const v of filter?.values || []) {
    const cv = listCheckboxValueFromQuery(v?.query, handle);
    if (cv !== '') allowed.add(cv);
  }
  return allowed;
};

/**
 * Build Hello Retail `products.filters` query strings from the browser URL, only when each value exists
 * in the facet options returned by Hello Retail for this collection.
 */
const buildValidatedHelloRetailFacetQueries = (rawParams, filterData) => {
  const queries = [];
  const filters = filterData || [];
  const keyToFilter = buildFacetParamToFilterMap(filters);
  const rangeFilter = filters.find((f) => f?.settings?.type === 'RANGE');

  const priceGte = rawParams.get('filter.v.price.gte');
  const priceLte = rawParams.get('filter.v.price.lte');
  if ((priceGte || priceLte) && rangeFilter) {
    const rMin = Number(rangeFilter.min ?? 0);
    const rMax = Number(rangeFilter.max ?? NaN);
    const minParsed =
      priceGte !== null && priceGte !== '' ? Number(priceGte) : rMin;
    const maxParsed =
      priceLte !== null && priceLte !== '' ? Number(priceLte) : rMax;
    const minBoundary = Number.isFinite(rMin) ? Math.floor(rMin) : rMin;
    const maxBoundary = Number.isFinite(rMax) ? Math.ceil(rMax) : rMax;
    const minOk = Number.isFinite(minParsed) && minParsed >= minBoundary;
    const maxOk =
      priceLte === null ||
      priceLte === '' ||
      (Number.isFinite(maxParsed) &&
        Number.isFinite(maxBoundary) &&
        maxParsed <= maxBoundary &&
        maxParsed >= minParsed);
    if (minOk && maxOk && Number.isFinite(maxBoundary)) {
      queries.push(`price:${priceGte || 0},${priceLte || ''}`);
    }
  }

  const ignoredForFacetValues = new Set([
    'page',
    'sort_by',
    'view',
    'section_id',
    'filter.v.price.gte',
    'filter.v.price.lte',
  ]);

  const resolveAllowedFacetValue = (allowed, rawValue) => {
    const decoded = decodeUrlFacetParamValue(rawValue);
    const norm = normalizeUrlFacetValue(decoded);
    if (allowed.has(norm)) return norm;
    const lower = norm.toLowerCase();
    for (const candidate of allowed) {
      if (String(candidate).toLowerCase() === lower) return candidate;
    }
    return null;
  };

  rawParams.forEach((value, key) => {
    if (ignoredForFacetValues.has(key) || value === '') return;
    const filter = keyToFilter.get(key);
    if (!filter) return;
    const type = filter?.settings?.type;
    if (type === 'RANGE') return;
    const allowed = collectAllowedFilterValuesFromHelloRetail(filter);
    if (allowed.size === 0) return;
    const canonical = resolveAllowedFacetValue(allowed, value);
    if (canonical != null) {
      queries.push(`${key}:${canonical}`);
    }
  });

  return queries;
};

const hrEscapeHtml = (str) =>
  String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

const HELLO_RETAIL_FACET_REMOVE_ICON = `<svg class="icon icon-xs" viewBox="0 0 10 10" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation"><path stroke-linecap="round" stroke-linejoin="round" d="m1 9 8-8M1 1l8 8"></path></svg>`;

const getHelloRetailFacetUrlKeysFromFilterData = (filterData) => {
  const keys = new Set(['filter.v.price.gte', 'filter.v.price.lte']);
  for (const f of filterData || []) {
    if (f?.settings?.name) keys.add(f.settings.name);
    if (f?.name) keys.add(f.name);
  }
  return keys;
};

const cloneSearchParamsRemovingKeys = (searchString, keysToRemove) => {
  const p = new URLSearchParams(searchString);
  for (const k of keysToRemove) {
    p.delete(k);
  }
  p.delete('page');
  return p;
};

const removeOneFacetValueFromSearch = (searchString, paramKey, rawValue) => {
  const p = new URLSearchParams();
  const toRemoveNorm = normalizeUrlFacetValue(decodeUrlFacetParamValue(rawValue));
  for (const [k, v] of new URLSearchParams(searchString).entries()) {
    if (k !== paramKey) {
      p.append(k, v);
      continue;
    }
    const norm = normalizeUrlFacetValue(decodeUrlFacetParamValue(v));
    if (norm !== toRemoveNorm) p.append(k, v);
  }
  p.delete('page');
  return p;
};

const removePriceRangeFromSearch = (searchString) => {
  const p = new URLSearchParams(searchString);
  p.delete('filter.v.price.gte');
  p.delete('filter.v.price.lte');
  p.delete('page');
  return p;
};

const helloRetailFilterIsColorSwatchFacet = (filter, enableColorSwatches) => {
  if (!enableColorSwatches) return false;
  const nameLower = String(filter?.settings?.name || '').toLowerCase();
  const titleLower = String(filter?.settings?.title || '').toLowerCase();
  const triggers = String(window.translate?.products?.color_swatch_trigger || '')
    .toLowerCase()
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
  for (const t of triggers) {
    if (t && (nameLower.includes(t) || titleLower.includes(t))) return true;
    if (t === 'color' && (nameLower.includes('colour') || titleLower.includes('colour'))) return true;
  }
  return false;
};

const findHelloRetailListValueMeta = (filter, canonicalValue) => {
  const handle = getHelloRetailFacetHandle(filter);
  const lower = String(canonicalValue).toLowerCase();
  for (const v of filter?.values || []) {
    const cv = listCheckboxValueFromQuery(v?.query, handle);
    if (cv === canonicalValue || String(cv).toLowerCase() === lower) {
      return { title: v?.title || cv, query: v?.query };
    }
  }
  return { title: canonicalValue, query: null };
};

const formatHelloRetailPricePillText = (gte, lte, currencySymbol, i18nFrom, i18nTo, i18nSep) => {
  const sym = currencySymbol || '';
  const fmt = (n, roundMode = 'nearest') => {
    const num = Number(n);
    if (!Number.isFinite(num)) return String(n);
    const rounded =
      roundMode === 'down'
        ? Math.floor(num)
        : roundMode === 'up'
          ? Math.ceil(num)
          : Math.round(num);
    return `${sym}${rounded.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };
  const min = gte != null && gte !== '' ? fmt(gte, 'down') : null;
  const max = lte != null && lte !== '' ? fmt(lte, 'up') : null;
  if (min && max) return `${min} ${i18nSep} ${max}`;
  if (min) return `${i18nFrom} ${min}`;
  if (max) return `${i18nTo} ${max}`;
  return '';
};

const resolveHelloRetailFacetCanonicalForDisplay = (allowed, rawValue) => {
  const decoded = decodeUrlFacetParamValue(rawValue);
  const norm = normalizeUrlFacetValue(decoded);
  if (allowed.has(norm)) return norm;
  const lower = norm.toLowerCase();
  for (const c of allowed) {
    if (String(c).toLowerCase() === lower) return c;
  }
  return null;
};

const readHelloRetailColorMetaForLabel = (valueLabel) => {
  const el = document.querySelector('#colorFilterData');
  if (!el) return { swatchColor: null, swatchImage: null };
  try {
    const colorData = JSON.parse(el.textContent);
    const match = colorData?.colors?.find(
      (c) => c?.label?.toLowerCase() === String(valueLabel).toLowerCase()
    );
    if (!match) return { swatchColor: null, swatchImage: null };
    return {
      swatchColor: match.image ? null : match.color || null,
      swatchImage: match.image || null,
    };
  } catch {
    return { swatchColor: null, swatchImage: null };
  }
};

/**
 * Active facet pills (mirrors snippets/facets-active.liquid structure for styling).
 */
const buildHelloRetailAppliedFacetsHtml = ({
  filterData,
  pathname,
  searchParams,
  sectionId,
  currencySymbol,
  enableColorSwatches,
}) => {
  const filters = filterData?.length ? filterData : [];
  if (!filters.length) return '';

  const keyToFilter = buildFacetParamToFilterMap(filters);
  const facetKeys = getHelloRetailFacetUrlKeysFromFilterData(filters);
  const ignoredKeys = new Set([...HR_NON_FACET_URL_KEYS, 'section_id']);

  const i18nFrom = window.translate?.collections?.filtering?.from_price || 'From';
  const i18nTo = window.translate?.collections?.filtering?.to_price || 'To';
  const i18nSep = window.translate?.collections?.filtering?.to || '–';
  const clearTpl =
    window.translate?.collections?.filtering?.clear || 'Remove filter {{ name }}';
  const clearAllLabel = window.translate?.collections?.filtering?.clear_all || 'Clear all';

  const rawParams =
    typeof searchParams === 'string' ? new URLSearchParams(searchParams) : searchParams;

  const pills = [];

  const rangeFilter = filters.find((f) => f?.settings?.type === 'RANGE');
  const priceGte = rawParams.get('filter.v.price.gte');
  const priceLte = rawParams.get('filter.v.price.lte');
  if ((priceGte || priceLte) && rangeFilter) {
    const rMin = Number(rangeFilter.min ?? 0);
    const rMax = Number(rangeFilter.max ?? NaN);
    const minParsed =
      priceGte !== null && priceGte !== '' ? Number(priceGte) : rMin;
    const maxParsed =
      priceLte !== null && priceLte !== '' ? Number(priceLte) : rMax;
    const minBoundary = Number.isFinite(rMin) ? Math.floor(rMin) : rMin;
    const maxBoundary = Number.isFinite(rMax) ? Math.ceil(rMax) : rMax;
    const minOk = Number.isFinite(minParsed) && minParsed >= minBoundary;
    const maxOk =
      priceLte === null ||
      priceLte === '' ||
      (Number.isFinite(maxParsed) &&
        Number.isFinite(maxBoundary) &&
        maxParsed <= maxBoundary &&
        maxParsed >= minParsed);
    if (minOk && maxOk && Number.isFinite(maxBoundary)) {
      const title = formatHelloRetailPricePillText(
        priceGte,
        priceLte,
        currencySymbol,
        i18nFrom,
        i18nTo,
        i18nSep
      );
      const removeParams = removePriceRangeFromSearch(rawParams.toString());
      const qs = removeParams.toString();
      const href = `${pathname}${qs ? `?${qs}` : ''}`;
      const ariaRaw = clearTpl.replace(/\{\{\s*name\s*\}\}/g, title);
      pills.push(`<div class="removable-facet flex items-center justify-between gap-3 lg:gap-4 text-xs lg:text-sm leading-none">
          <div class="facet-title flex items-center gap-2">${hrEscapeHtml(title)}</div>
          <a class="block relative" href="${hrEscapeHtml(href)}" aria-label="${hrEscapeHtml(ariaRaw)}" data-hr-facet-remove data-magnet="3" data-no-instant>
            <span class="btn-text" data-text>${HELLO_RETAIL_FACET_REMOVE_ICON}</span>
          </a>
        </div>`);
    }
  }

  rawParams.forEach((value, key) => {
    if (ignoredKeys.has(key) || key.startsWith('utm_') || value === '') return;
    if (key === 'filter.v.price.gte' || key === 'filter.v.price.lte') return;
    const filter = keyToFilter.get(key);
    if (!filter) return;
    const type = filter?.settings?.type;
    if (type === 'RANGE') return;
    const allowed = collectAllowedFilterValuesFromHelloRetail(filter);
    if (!allowed.size) return;
    const canonical = resolveHelloRetailFacetCanonicalForDisplay(allowed, value);
    if (canonical == null) return;

    const { title: valueLabel } = findHelloRetailListValueMeta(filter, canonical);
    const filterLabel = filter?.settings?.title || filter?.name || key;
    const displayLabel =
      type === 'BOOLEAN' ? `${filterLabel}: ${valueLabel}` : valueLabel;

    let swatchPrefix = '';
    if (helloRetailFilterIsColorSwatchFacet(filter, enableColorSwatches)) {
      const { swatchColor, swatchImage } = readHelloRetailColorMetaForLabel(valueLabel);
      let style = '';
      if (swatchImage) {
        const safeImg = String(swatchImage).replace(/\)/g, '%29');
        style = `--swatch-background: transparent; --swatch-background-image: url(${safeImg});`;
      } else if (swatchColor) {
        style = `--swatch-background: ${hrEscapeHtml(swatchColor)};`;
      } else {
        style = `--swatch-background: ${hrEscapeHtml(valueLabel)};`;
      }
      const imgClass = swatchImage ? ' with-image' : '';
      swatchPrefix = `<span class="swatch swatch--sm inline-block shrink-0${imgClass}" style="${style}" aria-hidden="true"></span>`;
    }

    const removeParams = removeOneFacetValueFromSearch(rawParams.toString(), key, value);
    const qs = removeParams.toString();
    const href = `${pathname}${qs ? `?${qs}` : ''}`;
    const ariaRaw = clearTpl.replace(/\{\{\s*name\s*\}\}/g, displayLabel);
    pills.push(`<div class="removable-facet flex items-center justify-between gap-3 lg:gap-4 text-xs lg:text-sm leading-none">
          <div class="facet-title flex items-center gap-2">${swatchPrefix}${hrEscapeHtml(displayLabel)}</div>
          <a class="block relative" href="${hrEscapeHtml(href)}" aria-label="${hrEscapeHtml(ariaRaw)}" data-hr-facet-remove data-magnet="3" data-no-instant>
            <span class="btn-text" data-text>${HELLO_RETAIL_FACET_REMOVE_ICON}</span>
          </a>
        </div>`);
  });

  if (!pills.length) return '';

  const clearAllParams = cloneSearchParamsRemovingKeys(rawParams.toString(), facetKeys);
  const clearAllQs = clearAllParams.toString();
  const clearAllHref = `${pathname}${clearAllQs ? `?${clearAllQs}` : ''}`;
  let clearAllHtml = '';
  if (pills.length > 1) {
    clearAllHtml = `<a href="${hrEscapeHtml(clearAllHref)}" class="facet-clear-all link text-xs lg:text-sm" data-hr-facet-remove-all data-magnet="0">${hrEscapeHtml(clearAllLabel)}</a>`;
  }

  const idAttr = sectionId
    ? ` id="HelloRetailFacetFiltersActive-${hrEscapeHtml(sectionId)}"`
    : '';
  return `<div class="active-facets flex flex-wrap items-center gap-2 z-1 relative"${idAttr}>${pills.join('')}${clearAllHtml}</div>`;
};

if (HELLO_RETAIL_COMPONENTS.collectionPage && !customElements.get('hello-retail-collection')) {
  customElements.define(
    'hello-retail-collection',
    class HelloRetail extends HTMLElement {
    async connectedCallback() {
      this.sectionId = this.getAttribute('data-section-id');
      if (new URLSearchParams(window.location.search).get('view') === 'fallback') {
        return;
      }

      this.hierarchy = this.getAttribute('data-hierarchy');
      this.collectionId = this.getAttribute('data-collection-id');
      this.pageId = this.getAttribute('data-page-id');
      this.apiKey = window.helloRetail.hello_retail_api_key;
      this.websiteId = window.helloRetail.hello_retail_website_uuid;
      this.predefinedMaterialColor = (this.getAttribute('data-hr-predefined-filters') || '').trim();
      this.filterCollapsed = this.getAttribute('data-hr-filter-collapsed') === 'true';
      this.filterCollapsedExceptions = (this.getAttribute('data-hr-filter-collapsed-exceptions') || '')
        .toLowerCase()
        .split(',')
        .map((exception) => exception.trim())
        .filter(Boolean);
      this.helloRetailUrl = `https://core.helloretail.com/serve/pages/${this.pageId}`;
      this.pageSize = parseInt(this.getAttribute('data-products-per-page'), 10) || 16;
      if (this.predefinedMaterialColor) {
        console.log("NOL - CHANGE COLOR")
        const initialParams = new URLSearchParams(window.location.search);
        if (!initialParams.has('extraDataList.materialColors')) {
          initialParams.set('extraDataList.materialColors', this.predefinedMaterialColor);
          const initialQs = initialParams.toString();
          window.history.replaceState({}, '', `${window.location.pathname}${initialQs ? `?${initialQs}` : ''}`);
        }
      }

      this.start = 0;
      this.activeFilterQueries = [];
      this.activeSorting = [];
      this.lastFilterDataForValidation = null;
      this.filterParamWhitelist = new Set(['filter.v.price.gte', 'filter.v.price.lte']);
      this.filterParamWhitelistReady = false;
      this.hasBootstrappedUrlFacets = false;
      this.filterApplyDebounceTimer = null;
      this.filterApplyDebounceMs = 750;
      this.pendingFilterApplyPayload = null;

      this.helloRetailSectionElement = this;
      this.filterDrawerElement = this.querySelector('.collection__topbar');
      this.filterMobileDrawerElement = this.querySelector('.facet-sticky');
      this.filterContainerElement = this.filterDrawerElement?.querySelector('[data-hr-facet-drawer-desktop]');
      this.filterMobileContainerElement = this.filterMobileDrawerElement?.querySelector('[data-hr-facet-drawer-mobile]');
      this.sortElementMobile = this.filterMobileDrawerElement?.querySelector('[data-hr-sort-select-mobile]');
      this.sortElement = this.querySelector('[data-hr-sort-container]');
      this.sortContainerElement = this.sortElement?.querySelector('[data-hr-sort-inner]');
      this.productGrid = this.querySelector('[data-hr-product-grid-list]');
      this.paginationElement = this.querySelector('[data-hr-pagination-host]');
      this.facetsActiveElement = this.querySelector('[data-hr-facets-active]');
      this.recommendations = null;
      this.filterSubmitButtons = this.querySelectorAll('[data-hr-apply-filters]');
      this.onCollectionSectionUpdated = this.handleCollectionSectionUpdated.bind(this);
      this.onFilterSubmit = this.handleFilterSubmit.bind(this);
      this.onFilterChange = this.handleFilterChange.bind(this);
      this.onPaginationClick = this.handlePaginationClick.bind(this);
      this.onHelloRetailSortInput = this.handleHelloRetailSortInput.bind(this);
      this.onHelloRetailAppliedFacetsClick = this.handleHelloRetailAppliedFacetsClick.bind(this);

      this.addEventListener('change', this.onHelloRetailSortInput);
      this.addEventListener('click', this.onHelloRetailAppliedFacetsClick);

      this.#loadCollectionAndRenderPage();
      
      document.addEventListener('collection:section-updated', this.onCollectionSectionUpdated);
      this.filterSubmitButtons.forEach((button) => {
        button.addEventListener('click', this.onFilterSubmit);
      });
      document.addEventListener('change', this.onFilterChange, true);
      this.paginationElement?.addEventListener('click', this.onPaginationClick);
    }

    async handleFilterSubmit(event) {
      const drawerElement = event.currentTarget?.closest('drawer-element');
      // Filters are already applied on control changes; this button should only close the drawer.
      // drawerElement?.hide?.();
    }

    async handleFilterChange(event) {
      if (event.type === 'input') return;
      const changedControl =
        event.target?.matches?.('input[name], select[name]')
          ? event.target
          : event.composedPath?.().find(
            (node) => node?.nodeType === Node.ELEMENT_NODE && node.matches?.('input[name], select[name]')
          );
      if (!changedControl) return;
      if (changedControl.disabled) return;
      if (!changedControl.closest('hello-retail-collection, drawer-element')) return;
      if (
        event.type === 'input' &&
        (changedControl.type === 'number' || changedControl.type === 'range')
      ) {
        // Let users finish typing/dragging price values before applying filters.
        return;
      }

      const filterRoot = changedControl.closest('[data-hr-facet-drawer-mobile], [data-hr-facet-drawer-desktop]');
      if (!filterRoot) return;

      const isMobileFilter = filterRoot.hasAttribute('data-hr-facet-drawer-mobile');
      this.pendingFilterApplyPayload = {
        filterRoot,
        mobileSortSelect: isMobileFilter ? this.sortElementMobile : null,
      };
      if (this.filterApplyDebounceTimer) {
        clearTimeout(this.filterApplyDebounceTimer);
      }
      this.filterApplyDebounceTimer = window.setTimeout(() => {
        this.filterApplyDebounceTimer = null;
        const payload = this.pendingFilterApplyPayload;
        this.pendingFilterApplyPayload = null;
        if (!payload) return;
        void this.applyFiltersFromControls(payload);
      }, this.filterApplyDebounceMs);
    }

    setFilterButtonLoading(button, isLoading) {
      if (!button) return;
      const buttonText = button.querySelector('.btn-text');
      if (buttonText && !button.dataset.defaultText) {
        button.dataset.defaultText = buttonText.textContent.trim();
      }

      if (buttonText) {
        buttonText.textContent = isLoading
          ? (window.translate?.general?.loading || 'Laden...')
          : (button.dataset.defaultText || buttonText.textContent);
      }

      button.toggleAttribute('aria-busy', isLoading);
      button.setAttribute('data-hr-loading', isLoading ? 'true' : 'false');
      button.disabled = isLoading;
    }

    isAllowedFilterParam(paramName) {
      if (!paramName) return false;
      if (!this.filterParamWhitelistReady) {
        // Before API filter metadata is loaded, only trust known filter params from controls.
        const fallbackWhitelist = this.getFilterParamFallbackWhitelist();
        return fallbackWhitelist.has(paramName);
      }
      return this.filterParamWhitelist.has(paramName);
    }

    getFilterParamFallbackWhitelist() {
      const fallbackWhitelist = new Set(['filter.v.price.gte', 'filter.v.price.lte']);
      this.querySelectorAll('input[name], select[name]').forEach((element) => {
        const paramName = element?.getAttribute?.('name');
        if (typeof paramName !== 'string' || paramName.length === 0) return;
        if (paramName === 'sort_by' || paramName === 'page') return;
        fallbackWhitelist.add(paramName);
      });
      return fallbackWhitelist;
    }

    updateFilterParamWhitelist(filterData = []) {
      const nextWhitelist = new Set(['filter.v.price.gte', 'filter.v.price.lte']);
      filterData.forEach((filter) => {
        const paramName = filter?.settings?.name;
        const filterName = filter?.name;

        if (typeof paramName === 'string' && paramName.length > 0) {
          nextWhitelist.add(paramName);
        }

        if (typeof filterName === 'string' && filterName.length > 0) {
          nextWhitelist.add(filterName);
        }
      });
      this.filterParamWhitelist = nextWhitelist;
      this.filterParamWhitelistReady = true;
    }

    async applyFiltersFromControls({ filterRoot, mobileSortSelect = null, shouldScroll = false, triggerButton = null } = {}) {
      if (!filterRoot) return;
      const drawerElement = filterRoot.closest('drawer-element');
      const drawerApplyButtons = drawerElement
        ? Array.from(drawerElement.querySelectorAll('[data-hr-apply-filters]'))
        : [];
      const loadingButtons = triggerButton
        ? Array.from(new Set([...drawerApplyButtons, triggerButton]))
        : drawerApplyButtons;

      loadingButtons.forEach((button) => this.setFilterButtonLoading(button, true));

      const formData = new FormData();
      filterRoot.querySelectorAll('input[name]:not([disabled]), select[name]:not([disabled])').forEach((element) => {
        if ((element.type === 'checkbox' || element.type === 'radio') && !element.checked) return;
        if (element.value === '') return;
        if (!this.isAllowedFilterParam(element.name)) return;
        if (element.name === 'filter.v.price.gte' || element.name === 'filter.v.price.lte') {
          const rawDefault = element.dataset.defaultValue;
          if (rawDefault != null && rawDefault !== '') {
            const numericValue = Number(element.value);
            const numericDefault = Number(rawDefault);
            if (
              Number.isFinite(numericValue) &&
              Number.isFinite(numericDefault) &&
              numericValue === numericDefault
            ) {
              return;
            }
          }
        }
        formData.append(element.name, element.value);
      });

      const nextParams = new URLSearchParams();
      formData.forEach((value, key) => {
        nextParams.append(key, value);
      });

      // Mobile sort select has no name attribute, so include it explicitly on apply.
      // Featured clears the sorter instead of adding one, so `sort_by` has to be
      // dropped from the merged params below as well — hence the flag.
      const clearsSortBy = mobileSortSelect?.value === HR_FEATURED_SORT_VALUE;
      if (mobileSortSelect?.value && !clearsSortBy) {
        nextParams.set('sort_by', mobileSortSelect.value);
      }

      try {
        const merged = new URLSearchParams(window.location.search);
        merged.delete('page');
        if (clearsSortBy) {
          merged.delete('sort_by');
        }
        const facetKeysToReset = this.filterParamWhitelistReady
          ? this.filterParamWhitelist
          : this.getFilterParamFallbackWhitelist();
        for (const key of facetKeysToReset) {
          merged.delete(key);
        }
        for (const key of new Set(nextParams.keys())) {
          merged.delete(key);
        }
        nextParams.forEach((value, key) => {
          merged.append(key, value);
        });

        const qs = merged.toString();
        const nextUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}`;
        window.history.replaceState({}, '', nextUrl);
        
        await this.#loadCollectionAndRenderPage();
        if (shouldScroll) {
          this.scrollToProductGrid();
        }
      } finally {
        loadingButtons.forEach((button) => this.setFilterButtonLoading(button, false));
      }
    }

    scrollToProductGrid() {
      const target = this.querySelector('.collection') || this.productGrid;
      if (!target) return;

      const headerElement = document.querySelector('header.header');
      const headerOffset = (!theme.config.mqlSmall && headerElement) ? Math.round(headerElement.clientHeight) : 0;
      const topOffset = (theme.config.isTouch || theme.config.mqlSmall ? 95 : 20) + headerOffset;

      window.scrollTo({
        top: target.getBoundingClientRect().top + window.scrollY - topOffset,
        behavior: theme.config.motionReduced ? 'auto' : 'smooth',
      });
    }

    /**
     * Desktop sort uses <facet-sort> with radios, but the theme's facet-sort only submits via
     * form[is="facet-form"] — absent on Hello Retail. Update the URL and reload the HR box here.
     * The mobile drawer's sort <select> sits outside the facet container, so the filter-change
     * handler never sees it; it is picked up here too.
     */
    handleHelloRetailSortInput(event) {
      const t = event.target;
      if (!this.contains(t)) return;
      if (t.closest?.('form[is="facet-form"]')) return;

      const isSortRadio = t?.matches?.('input[name="sort_by"]') && t.type === 'radio';
      const isMobileSortSelect = t?.matches?.('[data-hr-sort-select-mobile]');
      if (!isSortRadio && !isMobileSortSelect) return;
      if (isSortRadio && !t.checked) return;

      const params = new URLSearchParams(window.location.search);
      // Featured is HR's default ordering, expressed as the absence of a sorter.
      if (t.value === HR_FEATURED_SORT_VALUE) {
        params.delete('sort_by');
      } else {
        params.set('sort_by', t.value);
      }
      params.delete('page');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
      void this.#loadCollectionAndRenderPage();
    }

    handleHelloRetailAppliedFacetsClick(event) {
      const link = event.target.closest('[data-hr-facet-remove], [data-hr-facet-remove-all]');
      if (!link || !this.contains(link)) return;
      if (link.closest('form[is="facet-form"]')) return;
      event.preventDefault();
      const href = link.getAttribute('href');
      if (!href) return;
      const url = new URL(href, window.location.origin);
      window.history.replaceState({}, '', `${url.pathname}${url.search}`);
      void this.#loadCollectionAndRenderPage();
    }

    #updateHelloRetailAppliedFacets(filtersForUi) {
      if (!this.facetsActiveElement) return;
      if (this.getAttribute('data-hr-enable-filtering') !== 'true') {
        this.facetsActiveElement.innerHTML = '';
        return;
      }
      const enableSwatches = this.getAttribute('data-hr-filter-color-swatches') === 'true';
      const currency =
        this.getAttribute('data-hr-currency-symbol') || window.helloRetail?.currencySymbol || '';
      this.facetsActiveElement.innerHTML = buildHelloRetailAppliedFacetsHtml({
        filterData: filtersForUi,
        pathname: window.location.pathname,
        searchParams: window.location.search,
        sectionId: this.sectionId,
        currencySymbol: currency,
        enableColorSwatches: enableSwatches,
      });
    }

    async handlePaginationClick(event) {
      const paginationLink = event.target.closest('.pagination a[href]');
      if (!paginationLink) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      event.preventDefault();
      const paginationUrl = new URL(paginationLink.getAttribute('href'), window.location.origin);
      const nextUrl = `${paginationUrl.pathname}${paginationUrl.search}`;
      window.history.replaceState({}, '', nextUrl);

      await this.#loadCollectionAndRenderPage();
      this.scrollToProductGrid();
    }

    #applyPaginationAndSortFromUrl(rawParams) {
      const page = rawParams.get('page');
      const sortBy = rawParams.get('sort_by');
      this.start = 0;
      this.activeSorting = [];

      if (page) {
        const pageNumber = Math.max(parseInt(page, 10), 1);
        this.start = (pageNumber - 1) * this.pageSize;
      }

      // `featured` is our own marker for HR's default ordering — it is not a
      // sorter HR knows, so it must never reach the request.
      if (sortBy && sortBy !== HR_FEATURED_SORT_VALUE) {
        this.activeSorting = [sortBy];
      }
    }

    /**
     * First load with facet params in the URL: HR metadata is not available yet, so we fetch an unfiltered
     * page to obtain filters, validate URL values against those options, then refetch when needed.
     */
    async #fetchCollectionWithUrlFacetResolution(rawParams) {
      const urlHasFacetParams = rawUrlSearchParamsHasFacetLikeParams(rawParams);
      const hasFilterMetadata =
        Array.isArray(this.lastFilterDataForValidation) &&
        this.lastFilterDataForValidation.length > 0;
      /**
       * Custom elements can run connectedCallback during define() before any payload exists.
       * Whitelist may also be "ready" with an empty filter list — then we must still bootstrap
       * metadata when the URL contains facet params.
       */
      const facetBootstrap =
        urlHasFacetParams &&
        !this.hasBootstrappedUrlFacets &&
        (!this.filterParamWhitelistReady || !hasFilterMetadata);

      if (facetBootstrap) {
        this.hasBootstrappedUrlFacets = true;
        const savedStart = this.start;
        this.start = 0;
        this.activeFilterQueries = [];
        let collectionResults = await this.loadCollection();
        if (!collectionResults?.success) {
          return collectionResults;
        }

        const bootstrapFilters = collectionResults?.data?.products?.filters ?? [];
        if (bootstrapFilters.length > 0) {
          this.updateFilterParamWhitelist(bootstrapFilters);
        }
        this.lastFilterDataForValidation = bootstrapFilters;
        this.activeFilterQueries = buildValidatedHelloRetailFacetQueries(rawParams, bootstrapFilters);
        this.start = savedStart;

        const needsRefetch =
          this.activeFilterQueries.length > 0 || savedStart !== 0;

        if (needsRefetch) {
          collectionResults = await this.loadCollection();
        }
        return collectionResults;
      }

      this.activeFilterQueries = buildValidatedHelloRetailFacetQueries(
        rawParams,
        this.lastFilterDataForValidation ?? []
      );
      return await this.loadCollection();
    }

    async handleCollectionSectionUpdated(event) {
      const ownSortContainerId = this.sectionId ? `HelloRetailSortContainer-${this.sectionId}` : '';
      if (event?.detail?.sectionId !== ownSortContainerId) return;

      await this.#loadCollectionAndRenderPage();
    }

    async #loadCollectionAndRenderPage() {
      if (!this.pageId) {
        throw new Error('Box ID not found');
      }

      if (!this.websiteId) {
        throw new Error('Website ID not found');
      }

      if (!this.collectionId) {
        throw new Error('Collection ID not found');
      }

      // Set loading state on collection element
      this.setAttribute('aria-busy', 'true');
      this.setProductGridLoadingState(true);

      // Load the recommendations
      try {
        const rawParams = new URLSearchParams(window.location.search);
        this.#applyPaginationAndSortFromUrl(rawParams);
        const collectionResults = await this.#fetchCollectionWithUrlFacetResolution(rawParams);
        const hrProducts = collectionResults?.data?.products;
        const hrTotal = Number(hrProducts?.total ?? NaN);
        if (!collectionResults?.success || (collectionResults?.success === true && hrTotal === 0)) {
          this.removeAttribute('aria-busy');
          this.#redirectToFallbackView();
          return;
        }

        const allVariantIds = collectionResults?.data?.products?.result?.map(product => product.productNumber);
        const productInformationResults = await getProductInformation(allVariantIds);

        // Render productcards
        if (productInformationResults?.data) {
          this.productGrid.innerHTML = '';

          productInformationResults?.data?.forEach(product => {
            const enhanceProductInformation = collectionResults?.data?.products?.result.find(collectionProduct => collectionProduct.productNumber == product.id?.replace('gid://shopify/ProductVariant/', ''));
            
            const productCard = createProductCard(product, enhanceProductInformation, this.sectionId);

            this.productGrid.insertAdjacentHTML('beforeend', productCard);
          });

          this.renderPromotionBlocks();
        }

        const filterData = collectionResults?.data?.products?.filters ?? [];
        /** HR often omits `filters` on paginated/sorted responses; keep prior metadata for URL validation. */
        if (filterData.length > 0) {
          this.lastFilterDataForValidation = filterData;
        } else if (!(this.lastFilterDataForValidation?.length > 0)) {
          this.lastFilterDataForValidation = filterData;
        }

        const filtersForUi =
          filterData.length > 0 ? filterData : (this.lastFilterDataForValidation ?? []);

        // Render filters
        if (this.filterContainerElement) {
          this.filterContainerElement.innerHTML = '';
          this.filterMobileContainerElement.innerHTML = '';
          if (filtersForUi.length > 0) {
            this.updateFilterParamWhitelist(filtersForUi);
          }

          const desktopFilters = await this.loadFilterElements(filtersForUi, 'hr-desktop');
          const mobileFilters = await this.loadFilterElements(filtersForUi, 'hr-mobile');

          this.filterContainerElement.insertAdjacentHTML('beforeend', desktopFilters.join(''));
          this.filterMobileContainerElement.insertAdjacentHTML('beforeend', mobileFilters.join(''));
        } else {
          if (filterData.length > 0) {
            this.updateFilterParamWhitelist(filterData);
          }
        }

        this.#updateHelloRetailAppliedFacets(filtersForUi);

        // Render sort
        if (this.sortElement) {
          this.sortContainerElement.innerHTML = '';
          this.sortElementMobile.innerHTML = '';

          const sortData = collectionResults?.data?.products?.sorting;
          const preferredSort =
            this.activeSorting[0] ||
            new URLSearchParams(window.location.search).get('sort_by') ||
            undefined;
          const sortElements = await this.loadSortElements(sortData, preferredSort);
          const sortElementsMobile = await this.loadSortElementsMobile(sortData, preferredSort);

          this.sortContainerElement.insertAdjacentHTML('beforeend', sortElements);
          this.sortElementMobile.insertAdjacentHTML('beforeend', sortElementsMobile);
        }

        // Render pagination
        if (this.paginationElement) {
          this.paginationElement.innerHTML = '';
          const newPaginationElement = createPaginationElement(collectionResults?.data?.products, this.pageSize);

          this.paginationElement.insertAdjacentHTML('beforeend', newPaginationElement);
        }

        return { success: true, data: productInformationResults.data, message: 'Product information loaded successfully' };

      } catch (error) {
        this.removeAttribute('aria-busy');
        this.#redirectToFallbackView();
      } finally {
        this.setProductGridLoadingState(false);
        this.removeAttribute('aria-busy');
      }
    }

    setProductGridLoadingState(isLoading) {
      if (!this.productGrid) return;
      this.productGrid.classList.toggle('hr-product-grid--loading', isLoading);
      this.productGrid.toggleAttribute('aria-busy', isLoading);
      if (isLoading && this.productGrid.children.length === 0) {
        this.productGrid.innerHTML = createHelloRetailLoadingCards(this.pageSize);
      }
    }

    #redirectToFallbackView() {
      const fallbackUrl = new URL(window.location.href);
      fallbackUrl.searchParams.set('view', 'fallback');
      fallbackUrl.searchParams.delete('section_id');
      window.location.assign(fallbackUrl.toString());
    }

    async loadCollection() {
      const requestUrl = new URL(`${window.location.pathname}${window.location.search}`, window.location.origin);
      if (this.predefinedMaterialColor && !requestUrl.searchParams.has('extraDataList.materialColors')) {
        requestUrl.searchParams.set('extraDataList.materialColors', this.predefinedMaterialColor);
      }
      const requestFilters = {
        "extraDataList.collectionIds": this.collectionId,
      };
      if (this.predefinedMaterialColor) {
        requestFilters["extraDataList.materialColors"] = this.predefinedMaterialColor;
      }

      const body = {
        websiteUuid: this.websiteId,
        url: `${requestUrl.pathname}${requestUrl.search}`,
        format: "json",
        firstLoad: true,
        params: {
          filters: requestFilters
        },
        products: {
          start: this.start,
          count: this.pageSize,
          sorting: this.activeSorting,
          filters: this.activeFilterQueries,
          fields: [
            "title",
            "originalUrl",
            "trackingCode",
            "productNumber",
            "extraData.handle"
          ],
        },
      };
      const tid = getTrackingIdCookie();
      if (tid) {
        body.trackingUserId = tid;
      }
      try {
        const response = await fetch(this.helloRetailUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }); 
  
        if (!response.ok) {
          return { success: false, status: response.status, message: response.statusText };
        }
  
        const json = await response.json();

        return { success: true, data: json, message: 'Recommendations loaded successfully' };
      } catch (error) {
        return { success: false, status: 500, message: error.message, data: null };
      }
    }

    renderPromotionBlocks() {
      if (!this.productGrid || this.productGrid.children.length === 0) return;

      const promotionTemplates = Array.from(this.querySelectorAll('template[data-hr-promotion-template]'))
        .map((template) => ({
          template,
          position: parseInt(template.getAttribute('data-position'), 10),
        }))
        .filter(({ position }) => Number.isInteger(position) && position > 0)
        .sort((a, b) => a.position - b.position);

      promotionTemplates.forEach(({ template, position }) => {
        const insertIndex = position - 1;
        if (insertIndex >= this.productGrid.children.length) return;

        const referenceNode = this.productGrid.children[insertIndex];
        this.productGrid.insertBefore(template.content.cloneNode(true), referenceNode);
      });
    }

    async loadFilterElements(filterData, instanceSuffix = '') {
      const filterElements = [];

      filterData.forEach(filter => {
        const isCollapsed = helloRetailFilterIsCollapsed(
          filter,
          this.filterCollapsed,
          this.filterCollapsedExceptions,
        );

        // Sizes come back in Hello Retail's own order; sort them before rendering.
        const filterForUi = sortHelloRetailFilterValues(filter);

        if (filter.settings?.type === 'RANGE') {
          const priceRangeFilter = createPriceRangeFilter(filterForUi, instanceSuffix, isCollapsed);

          filterElements.push(priceRangeFilter);
        } else if (filter.settings?.type === 'BOOLEAN') {
          const booleanFilter = createBooleanFilter(filterForUi, instanceSuffix);

          filterElements.push(booleanFilter);
        } else if (filter.settings?.type === 'LIST') {
          if (filter.settings?.name?.toLowerCase().includes('color') || window.translate.products.color_swatch_trigger?.toLowerCase().includes(filter.settings?.name?.toLowerCase())) {
            filterElements.push(createColorFilter(filterForUi, instanceSuffix, isCollapsed));
          } else {
            filterElements.push(createListFilter(filterForUi, instanceSuffix, isCollapsed));
          }
        }
      });
      
      return filterElements;
    }

    async loadSortElements(sortData, preferredSortValue) {
      return createSortElement(sortData, preferredSortValue);
    }

    async loadSortElementsMobile(sortData, preferredSortValue) {
      return createSortElementMobile(sortData, preferredSortValue);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onHelloRetailSortInput);
      this.removeEventListener('click', this.onHelloRetailAppliedFacetsClick);
      document.removeEventListener('collection:section-updated', this.onCollectionSectionUpdated);
      this.filterSubmitButtons?.forEach((button) => {
        button.removeEventListener('click', this.onFilterSubmit);
      });
      if (this.filterApplyDebounceTimer) {
        clearTimeout(this.filterApplyDebounceTimer);
        this.filterApplyDebounceTimer = null;
      }
      this.pendingFilterApplyPayload = null;
      document.removeEventListener('change', this.onFilterChange, true);
      this.paginationElement?.removeEventListener('click', this.onPaginationClick);
      console.log('disconnectedCallback', this);
    }
    },
  );
}

// Product card stuff
const createHelloRetailLoadingCards = (count = 8) => {
  const safeCount = Math.max(Number(count) || 0, 1);
  return Array.from({ length: safeCount })
    .map(
      () => `
    <article class="card product-card product-card--loading flex flex-col leading-none relative" aria-hidden="true">
      <div class="product-card__media relative h-auto">
        <div class="media media--${window.productCardInfo.image_ratio || 'portrait'} overflow-hidden">
          <div class="hr-loading-block hr-loading-block--media w-full h-full"></div>
        </div>
      </div>
      <div class="product-card__content grow flex flex-col justify-start gap-3 p-4">
        <div class="hr-loading-block hr-loading-block--line hr-loading-block--line-lg"></div>
        <div class="hr-loading-block hr-loading-block--line hr-loading-block--line-md"></div>
      </div>
    </article>`
    )
    .join('');
};

const createProductCard = (productInformation, enhanceProductInformation, sectionId) => {
  // if (!enhanceProductInformation) {
  //   return '';
  // }

  const variantImage = productInformation?.image;
  const variantId = enhanceProductInformation?.productNumber;
  let variantUrl = enhanceProductInformation?.originalUrl ? new URL(enhanceProductInformation?.originalUrl) : null;
  const productId = productInformation?.product?.id?.replace('gid://shopify/Product/', '');
  const productAvailableForSale = productInformation?.availableForSale;
  const showVendor = window.productCardInfo.show_vendor == "true";
  const cardBorderThickness = window.productCardInfo.card_border_thickness ? Number(window.productCardInfo.card_border_thickness) : 0;
  const hoverImage = productInformation?.hoverImage?.references?.nodes?.[1]?.previewImage;

  if (variantUrl === null) {
    variantUrl = new URL(window.location.href);
    variantUrl.pathname = `${locale}/products/${enhanceProductInformation?.extraData?.handle}`;
    variantUrl.searchParams.set('variant', variantId);
    variantUrl.hash = `aw_source=${enhanceProductInformation?.trackingCode}`;
  } else {
    variantUrl.hash = `aw_source=${enhanceProductInformation?.trackingCode}`;
  }

  let productCardClass = `card product-card product-card--${window.productCardInfo.card_style} flex flex-col leading-none relative`
  let productCardContent = 'product-card__content grow flex flex-col'

  if (cardBorderThickness > 0) {
    productCardClass += ` product-card--thickness`;
  }

  if (variantImage) {
    // Perfect, keep going
  } else {
    productCardClass += ` no-media`;
  }

  if (variantImage) {
    productCardContent += ` justify-start text-${window.productCardInfo.card_text_alignment} w-full`;
  } else {
    productCardContent += ` justify-center items-center absolute top-0 left-0 w-full h-full`;
  }

  const pricingHtml = getPricingHtml(productInformation);
  const variantSwitcherHtml = getVariantSwitcherHtml(productInformation, variantId);
  const basicProductInfoHtml = renderBasicProductInfoHtml(productInformation);
  const badgesHtml = getBadgesHtml(productInformation, productInformation);
  const badgeVariantDataHtml = renderProductBadgeInfoHtml(productInformation);

  return `
    <product-card 
      aria-busy="false" 
      data-section-id="${sectionId}" 
      data-product-id="${productId}" 
      data-current-variant-id="${variantId}"
      class="${productCardClass}"
      data-sold-out-text="${window.translate.products.sold_out || 'Uitverkocht'}"
      data-add-to-cart-text="${window.translate.products.add_to_cart || 'Toevoegen aan winkelwagen'}"
    >
      ${variantImage ? `
        <div class="product-card__media relative h-auto">
          <div class="badges z-2 absolute grid gap-3 pointer-events-none">
            ${badgesHtml}
          </div> 
        
          <a class="block relative product-card__media-slider media media--${window.productCardInfo.image_ratio}${window.productCardInfo.image_fill == 'true' ? "media--contain" : ""} overflow-hidden" href="${variantUrl?.toString()}" aria-hidden="true" tabindex="-1">
            <template>
              <div class="product-card__secondary media media--height w-full h-full overflow-hidden">
                <img src="${hoverImage?.url}" alt="${productInformation?.title}" loading="lazy" is="lazy-image" widths="180,360,540,720,900,1080" class="product-card__secondary media media--height w-full h-full overflow-hidden">
              </div>
            </template>

            <img src="${variantImage?.url}" alt="${productInformation?.title}" loading="lazy" is="lazy-image" widths="180,360,540,720,900,1080" class="product-card__media ${hoverImage ? 'hide-on-hover' : ''}">
            
            ${hoverImage ? `
              <div class="product-card__secondary media media--height w-full h-full overflow-hidden">
                <img src="${hoverImage?.url}" alt="${productInformation?.title}" loading="lazy" is="lazy-image" widths="180,360,540,720,900,1080" class="product-card__secondary media media--height w-full h-full overflow-hidden">
              </div>
            ` : ''}
          </a>
          ${window.productCardInfo.show_quick_add ? `
            <div class="quick-add ${productAvailableForSale ? 'flex' : 'hidden'} justify-end md:justify-center absolute w-full z-1 pointer-events-none" data-dynamic-quick-add="true">
              <form method="post" action="/cart/add" id="QuickAddProductForm-${sectionId}-${productId}" accept-charset="UTF-8" class="shopify-product-form" enctype="multipart/form-data" is="product-form">
                <input type="hidden" name="form_type" value="product">
                <input type="hidden" name="utf8" value="✓">
                <input type="hidden" name="id" value="${variantId}">
                <input type="hidden" name="product-id" value="${productId}">
                <input type="hidden" name="section-id" value="${sectionId}">
                <button type="submit" class="button button--primary pointer-events-auto" data-variant-id="${variantId}" aria-label="${productInformation?.title} toevoegen aan winkelwagen">
                  <span class="btn-fill" data-fill=""></span>
                  <span class="btn-text">
                    <svg class="icon icon-cart icon-sm" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation">
                      <path stroke-miterlimit="10" d="M7.1802 7.58C7.1802 4.36 9.2402 1.75 11.7902 1.75C14.3402 1.75 16.4002 4.36 16.4002 7.58M11.7902 7.58C2.0402 7.58 0.690197 6.97 2.3502 16.18C3.3002 21.48 3.9802 22.22 11.7902 22.22C19.6002 22.22 20.2802 21.47 21.2302 16.18C22.8902 6.97 21.5502 7.58 11.7902 7.58Z"></path>
                    </svg>
                  </span>
                </button>
              </form>
            </div>
          ` : ``}

        </div>
      ` : `
        <div class="product-card__media h-auto overflow-hidden">
          <span class="block aspect-${window.productCardInfo.image_ratio}"></span>
        </div>
      `}

      <div class="${productCardContent}">
        ${showVendor ? `
          <div class="product-card__top w-full">
            <div title="${productInformation?.product?.vendor}" class="caption reversed-link uppercase leading-none tracking-widest">
              <span class="sr-only">{{ 'general.accessibility.vendor' | t }}</span>
              ${productInformation?.product?.vendor || ''}
            </div>
          </div>
        ` : ''}

        <div class="product-card__details flex flex-col items-baseline gap-2 w-full">
          <p class="${window.productCardInfo.title_truncation == "true" ? `line-clamp-${window.productCardInfo.title_line_limit}` : ''}">
            <a class="product-card__title reversed-link text-base-xl font-medium leading-tight" href="${variantUrl?.toString()}">
              ${productInformation?.product?.title || ''}
            </a>
          </p>

          ${pricingHtml}
        </div>
        
        ${variantSwitcherHtml}
      </div>
      ${basicProductInfoHtml}
      ${badgeVariantDataHtml}
      <script type="application/json" id="ProductHoverImages">
        [${productInformation?.product?.variants?.nodes?.map((variant) => {
          return `
            {
              "id": ${variant?.id?.replace('gid://shopify/ProductVariant/', '')}
              ${variant?.hoverImage ? `, "hoverImage": "${variant?.hoverImage?.references?.nodes?.[1]?.previewImage?.url}"` : ''}
            }
          `;
        }).join(',')}]
      </script>
    </product-card>
  `;
}

const getPricingHtml = (productInformation) => {
  const priceAmount = productInformation?.price?.amount;
  const priceCurrencyCode = productInformation?.price?.currencyCode;
  const price = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: priceCurrencyCode }).format(priceAmount);

  const compareAtPriceAmount = productInformation?.compareAtPrice?.amount || null;
  const compareAtPriceCurrencyCode = productInformation?.compareAtPrice?.currencyCode;
  const compareAtPrice = compareAtPriceAmount ? new Intl.NumberFormat('nl-NL', { style: 'currency', currency: compareAtPriceCurrencyCode }).format(compareAtPriceAmount) : null;

  const pricingHtml = `
    <div class="flex flex-col gap-2" data-product-price>
      <div class="price ${compareAtPriceAmount !== null && compareAtPriceAmount !== priceAmount ? 'price--on-sale' : ''} gap-2">
        ${compareAtPriceAmount !== null && compareAtPriceAmount !== priceAmount ? `
          <span class="sr-only">${window.translate.products.sale_price}</span>
        ` : ''}
        <span class="price__regular whitespace-nowrap">
          ${price}
        </span>
        ${compareAtPriceAmount !== null && compareAtPriceAmount !== priceAmount ? `
          <span class="sr-only">${window.translate.products.regular_price}</span>
          <span class="price__sale inline-flex items-center h-auto relative">
            ${compareAtPrice}
          </span>
        ` : ''}
      </div>
    </div>
  `;
  return pricingHtml;
}

const getBadgesHtml = (productInformation, variantInformation = productInformation) => {
  const isSoldOut = variantInformation?.availableForSale !== true;
  // One badge is shown. A discounted product leads with its sale label — the one
  // whose title carries the %s placeholder — wherever it sits in the list, and
  // otherwise the first regular label takes the slot (mirrors product-badges.liquid).
  const badgeNodes = variantInformation?.product?.badges?.references?.nodes || [];
  const badgeTitleOf = (node) => node?.fields?.find((field) => field?.key === 'title')?.value || '';
  const hasDiscount = Number(variantInformation?.compareAtPrice?.amount) > Number(variantInformation?.price?.amount);
  const saleBadgeNode = hasDiscount ? badgeNodes.find((node) => badgeTitleOf(node).includes('%s')) : null;
  const regularBadgeNode = badgeNodes.find((node) => !badgeTitleOf(node).includes('%s'));
  const badgeObject = (saleBadgeNode || regularBadgeNode || badgeNodes[0])?.fields || null;

  if (isSoldOut) {
    return `
      <div class="badge badge--soldout flex items-center gap-1d5 font-medium leading-none rounded-full">
        ${window.translate.products.sold_out || 'Uitverkocht'}
      </div>
    `
  } else if (badgeObject) {
    const backgroundColor = badgeObject?.find((badge) => badge?.key === 'background_color')?.value;
    const textColor = badgeObject?.find((badge) => badge?.key === 'text_color')?.value;
    let title = badgeObject?.find((badge) => badge?.key === 'title')?.value;

    if (title.includes('%s') && variantInformation?.compareAtPrice?.amount) {
      let discountPercentage = (Number(variantInformation?.compareAtPrice?.amount) - Number(variantInformation?.price?.amount)) / Number(variantInformation?.compareAtPrice?.amount) * 100;
      if (discountPercentage > 0) {
        title = title.replace('%s', discountPercentage.toFixed(0) + '%');
      } else {
        return '';
      }
    } else if (title.includes('%s')) {
      return '';
    }

    if (title.includes('%s') && productInformation?.compareAtPrice?.amount) {
      const discountPercentage = (productInformation?.compareAtPrice?.amount - productInformation?.price?.amount) / productInformation?.compareAtPrice?.amount * 100;
      title = title.replace('%s', discountPercentage.toFixed(0) + '%');
    } else if (title.includes('%s')) {
      return '';
    }

    return `
      <div class="badge flex items-center gap-1d5 font-medium leading-none rounded-full" style="background-color: ${backgroundColor}; color: ${textColor};">
        ${title}
      </div>
    `
  } 

  return ''
}

const getVariantSwitcherHtml = (productInformation, variantId) => {
  return `
    <form data-current-variant-id="${variantId}" class="product-card__bottom product-card__bottom-variants flex items-center gap-2" id="variant-switcher">
      ${window.productCardInfo.show_color_swatches == "true" ? `
        ${productInformation?.product?.options?.map((option) => {
    let colorTriggerList = [window.translate.products.color_swatch_trigger?.toLowerCase(), 'color', 'colour'];

    const isColorOption = colorTriggerList.some(trigger => option.name.toLowerCase() == trigger);
    const forceColorSwatches = window.productCardInfo.color_swatches_only === 'true';
    const treatAsColor = isColorOption || forceColorSwatches;

    if (isColorOption) {
      return `
              <div class="product-card__bottom w-full flex items-center">
                <div class="swiper-button swiper-button-prev">
                  <svg width="11" height="24" viewBox="0 0 11 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.41699 18.6665L0.750364 11.9999L7.41699 5.33319" stroke="#1D1D1B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div class="swatches-slider swiper swatches swatches--color swatches--${window.productCardInfo.rounded_swatch} ${window.productCardInfo.product_color_swatch_type == 'variant' ? 'swatches--variant' : ''} swatches--${productInformation?.id} inline-flex items-start gap-2">
                <ul class="swiper-wrapper">
                  ${option.optionValues.map((value) => {
                    return getSwatchHtml(productInformation, value, true);
                  }).join('')}
                </ul>
                </div>
                <div class="swiper-button swiper-button-next">
                  <svg width="10" height="24" viewBox="0 0 10 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 5.3335L8.66663 12.0001L2 18.6668" stroke="#1D1D1B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
            `;
    } else {
      return `
              <div class="product-card__bottom w-full flex items-center">
                <div class="swiper-button swiper-button-prev">
                  <svg width="11" height="24" viewBox="0 0 11 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7.41699 18.6665L0.750364 11.9999L7.41699 5.33319" stroke="#1D1D1B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>

                <div class="swatches-slider swiper swatches ${forceColorSwatches ? 'swatches--color' : 'w-full'} swatches--${window.productCardInfo.rounded_swatch} ${window.productCardInfo.product_color_swatch_type == 'variant' ? 'swatches--variant' : ''} swatches--${productInformation?.id} inline-flex items-start gap-2">
                  <ul class="swiper-wrapper">
                    ${option.optionValues.map((value) => {
        return getSwatchHtml(productInformation, value, forceColorSwatches);
      }).join('')}
                  </ul>
                </div>

                <div class="swiper-button swiper-button-next">
                  <svg width="10" height="24" viewBox="0 0 10 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2 5.3335L8.66663 12.0001L2 18.6668" stroke="#1D1D1B" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
              </div>
            `;
    }
  }).join('')}
      ` : ''}
    </form>
  `;
}

const getSwatchHtml = (productInformation, value, isColorSwatch = false) => {
  const valueId = value?.id?.replace('gid://shopify/ProductOptionValue/', '');
  const valueName = value?.name
  const swatch = value?.swatch;
  let swatchImage = swatch?.image?.previewImage?.url || null;
  const splitProductTitle = productInformation?.title?.split(' / ');
  
  const selected = splitProductTitle?.some(title => title?.toLowerCase() == valueName?.toLowerCase());
  const firstSelectableVariant = value?.firstSelectableVariant;
  const isSoldOut = !firstSelectableVariant || firstSelectableVariant.availableForSale === false;
  const firstSelectableVariantImage = firstSelectableVariant?.image?.url;

  const secondaryColorMetaobject = firstSelectableVariant?.secondaryFilterColors?.references?.nodes?.[0]?.fields;
  let secondaryFilterColor = null;
  let secondaryFilterImage = null;
  if (secondaryColorMetaobject) {
    const colorField = secondaryColorMetaobject.find(f => f.key === 'color' || f.key === 'kleur');
    const imageField = secondaryColorMetaobject.find(f => f.key === 'image' || f.key === 'afbeelding');
    secondaryFilterColor = colorField?.value || null;
    secondaryFilterImage = imageField?.reference?.image?.url || null;
  }

  // Only fall back to the variant image in "Variant image" mode — in "Color
  // swatch" mode the swatch comes from the option value's own swatch or from
  // the variant_color_fallback metaobject below (mirrors product-card.liquid).
  const useVariantImage = window.productCardInfo?.product_color_swatch_type === 'variant';
  if (swatchImage === null && firstSelectableVariantImage && isColorSwatch === false && useVariantImage) {
    swatchImage = firstSelectableVariantImage;
  }

  const swatchColor = swatch?.color || secondaryFilterColor || null;
  if (swatchImage === null && secondaryFilterImage) {
    swatchImage = secondaryFilterImage;
  }

  // A non-colour swatch that resolved to a colour instead of an image gets
  // with-color, so the CSS renders it as a round material-colour swatch rather
  // than the square image tile / text pill (mirrors product-card.liquid).
  const isColorOnly = !swatchImage && !isColorSwatch && Boolean(swatchColor);

  return `
    <li class="swiper-slide ${swatchImage ? 'with-image' : ''}${isColorOnly ? ' with-color' : ''}">
      <input
        class="sr-only${isSoldOut ? ' disabled' : ''}"
        name=""
        value="${valueName}"
        type="radio"
        id="variant-switcher-${productInformation?.id}-${valueId}-${valueName}"
        ${selected ? 'checked' : ''}
        ${isSoldOut ? 'disabled' : ''}
      >
      <label
        data-value="${valueName}"
        class="swatch block relative ${isColorSwatch ? 'color-swatch' : ''} ${swatchImage ? 'with-image' : ''} ${selected ? 'selected' : ''} ${isSoldOut ? 'sold-out' : ''}"
        for="variant-switcher-${productInformation?.id}-${valueId}-${valueName}"
        style="${swatchColor ? '--swatch-background: ' + swatchColor + ';' : ''} ${swatchImage ? '--swatch-background-image: url(' + swatchImage + ');' : ''}"
        ><span class="sr-only">${valueName}</span></label
      >
    </li>
  `;
}

const renderBasicProductInfoHtml = (productInformation) => {

  return `
    <script type="application/json" id="BasicProductInfo">
      ${JSON.stringify(productInformationToLiquidJson(productInformation), null, 2)}
    </script>
  `;
}

const renderProductBadgeInfoHtml = (productInformation) => {
  const allVariantBadgeData = (productInformation?.product?.variants?.nodes || [])
    .map((variant) => {
      const variantId = parseShopifyGid(variant?.id);
      if (!variantId) return null;

      return {
        id: variantId,
        badgesHtml: `<div class="badges z-2 absolute grid gap-3 pointer-events-none">${getBadgesHtml(productInformation, variant)}</div>`,
      };
    })
    .filter(Boolean);

  return `
    <script type="application/json" id="ProductBadgeInfo">
      ${JSON.stringify(allVariantBadgeData, null, 2)}
    </script>
  `;
}

const parseShopifyGid = (gid) => {
  if (!gid || typeof gid !== 'string') return null;
  const m = gid.match(/\/(\d+)\s*$/);
  return m ? Number(m[1]) : null;
};

const toLiquidCdnSrc = (url) => {
  if (!url) return null;
  return url.replace(/^https?:/, '');
};

const moneyToCents = (money) => {
  if (!money || money.amount == null) return null;
  const n = Number.parseFloat(String(money.amount));
  if (Number.isNaN(n)) return null;
  return Math.round(n * 100);
};

const variantTitleToOptions = (title, optionGroupCount) => {
  const t = (title || '').trim();
  const count = Math.max(1, optionGroupCount || 1);

  if (count === 1) {
    return {
      option1: t || null,
      option2: null,
      option3: null,
      options: t ? [t] : [],
    };
  }

  const parts = t.split(/\s*\/\s*/).map((p) => p.trim()).filter(Boolean);
  const o1 = parts[0] ?? null;
  const o2 = parts[1] ?? null;
  const o3 = parts[2] ?? null;

  return {
    option1: o1,
    option2: count >= 2 ? (o2 ?? null) : null,
    option3: count >= 3 ? (o3 ?? null) : null,
    options: parts.length ? parts : [],
  };
};

const graphQlVariantNodeToLiquidVariant = (variantNode, product, optionGroupCount, resolvedVariantId) => {
  const productId = parseShopifyGid(product?.id);
  const variantId = resolvedVariantId ?? parseShopifyGid(variantNode?.id);
  const imageId = parseShopifyGid(variantNode?.image?.id);
  const src = toLiquidCdnSrc(variantNode?.image?.url);

  const { option1, option2, option3, options } = variantTitleToOptions(
    variantNode?.title ?? variantNode?.name,
    optionGroupCount
  );

  const variantTitle = (variantNode?.title || '').trim();
  const productTitle = product?.title || '';
  const publicTitle = variantTitle;
  const name = variantTitle ? `${productTitle} - ${variantTitle}` : productTitle;

  const price = moneyToCents(variantNode?.price);
  const compareAt = moneyToCents(variantNode?.compareAtPrice);

  return {
    id: variantId,
    title: variantTitle || publicTitle,
    option1,
    option2,
    option3,
    requires_shipping: true,
    taxable: true,
    featured_image: src
      ? {
        id: imageId,
        product_id: productId,
        position: 1,
        src,
        variant_ids: variantId != null ? [variantId] : [],
      }
      : null,
    available: Boolean(variantNode?.availableForSale),
    name,
    public_title: publicTitle,
    options,
    price,
    compare_at_price: compareAt,
    featured_media: src
      ? {
        alt: null,
        id: imageId,
        position: 1,
        preview_image: {
          aspect_ratio: null,
          height: null,
          width: null,
          src,
        },
      }
      : null,
    requires_selling_plan: false,
    selling_plan_allocations: [],
    quantity_rule: {
      min: 1,
      max: null,
      increment: 1,
    },
  };
};

const resolveVariantNumericId = (productInformation, node) => {
  const fromNode = parseShopifyGid(node?.id);
  if (fromNode != null) return fromNode;

  const rootId = parseShopifyGid(productInformation?.id);
  if (rootId == null) return null;

  const rootTitle = (productInformation?.title || '').trim();
  const nodeTitle = (node?.title || '').trim();
  const rootImg = productInformation?.image?.url;
  const nodeImg = node?.image?.url;

  if (rootTitle && nodeTitle && rootTitle === nodeTitle) return rootId;
  if (rootImg && nodeImg && rootImg === nodeImg) return rootId;

  return null;
};

const productInformationToLiquidJson = (productInformation) => {
  const product = productInformation?.product;
  const nodes = product?.variants?.nodes;
  if (!Array.isArray(nodes) || !product) {
    return [];
  }

  const optionGroupCount = Array.isArray(product.options) ? product.options.length : 1;

  return nodes.map((node) =>
    graphQlVariantNodeToLiquidVariant(
      node,
      product,
      optionGroupCount,
      resolveVariantNumericId(productInformation, node)
    )
  );
};

if (typeof module !== 'undefined' && module.es) {
  module.es = {
    productInformationToLiquidJson,
    parseShopifyGid,
    toLiquidCdnSrc,
    moneyToCents,
  };
}

const createCategoryCard = (allCategories) => {
  const categoryCards = allCategories.map(category => {
    return `
      <li>
        <a class="reversed-link text-base md:text-lg leading-tight font-medium" href="${category?.originalUrl}#aw_source=${category?.trackingCode}">
          ${category?.title}
        </a>
      </li>
    `;
  });

  return `
    <div class="grid gap-3 md:gap-4 w-full">
      <p class="search__heading block text-xs uppercase leading-tight tracking-widest">
        ${window.translate.general.search.collections || 'Collecties'}
      </p>
      <ul class="grid gap-2">
        ${categoryCards.join('')}
      </ul>
    </div>
  `;
}

// Filter stuff
const escapeHtml = (str) =>
  String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Mirror of the `collapsed` / `collapsed_exceptions` logic in snippets/facets-custom.liquid:
 * every filter group starts collapsed, except the params listed as exceptions.
 */
const helloRetailFilterIsCollapsed = (filter, collapsed, collapsedExceptions = []) => {
  if (!collapsed) return false;
  if (!collapsedExceptions.length) return true;

  const candidates = [filter?.settings?.name, filter?.name, getHelloRetailFacetHandle(filter)]
    .filter((value) => typeof value === 'string' && value.length > 0)
    .map((value) => value.toLowerCase());

  return !candidates.some((candidate) => collapsedExceptions.includes(candidate));
};

/** Attributes that keep a filter accordion open, matching the Liquid `details` markup. */
const helloRetailDetailsOpenAttrs = (isCollapsed) => ({
  className: isCollapsed ? 'details' : 'details active',
  openAttr: isCollapsed ? '' : ' open',
});

/** Prefix DOM ids so desktop + mobile filter copies do not share ids (breaks label[for]). */
const filterInstanceIdPrefix = (instanceSuffix) =>
  instanceSuffix ? `${String(instanceSuffix).replace(/[^\w-]/g, '')}-` : '';

/** Filter params whose values get the custom size ordering instead of Hello Retail's own order. */
const HR_SIZE_FILTER_PARAMS = new Set(['extradatalist.sizes']);

/** Letter sizes, smallest first; anything not listed falls back to the trailing "other" group. */
const HR_LETTER_SIZE_ORDER = ['xxxs', 'xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl', 'xxxxl'];

/** Normalise "2XL" / "3 XL" style labels to the repeated-x spelling used in HR_LETTER_SIZE_ORDER. */
const normaliseHelloRetailLetterSize = (label) => {
  const compact = label.replace(/[\s._/-]/g, '');
  const multiplier = compact.match(/^(\d)x([sl])$/);
  if (multiplier) {
    return `${'x'.repeat(Number(multiplier[1]))}${multiplier[2]}`;
  }
  return compact;
};

/**
 * Rank a size label into [group, numeric value] so sizes sort as:
 * plain numbers (18, 19, 20) -> MM -> CM -> S/M/L/XL -> anything else.
 * Within every group the values run from small to large.
 */
const helloRetailSizeSortKey = (label) => {
  const raw = String(label ?? '').trim().toLowerCase();
  const normalisedNumber = raw.replace(',', '.');

  const plain = normalisedNumber.match(/^(\d+(?:\.\d+)?)$/);
  if (plain) return [0, Number(plain[1])];

  const millimetre = normalisedNumber.match(/^(\d+(?:\.\d+)?)\s*mm$/);
  if (millimetre) return [1, Number(millimetre[1])];

  const centimetre = normalisedNumber.match(/^(\d+(?:\.\d+)?)\s*cm$/);
  if (centimetre) return [2, Number(centimetre[1])];

  const letterIndex = HR_LETTER_SIZE_ORDER.indexOf(normaliseHelloRetailLetterSize(raw));
  if (letterIndex !== -1) return [3, letterIndex];

  return [4, Number.POSITIVE_INFINITY];
};

/** True when this filter is the size facet that needs the custom ordering. */
const isHelloRetailSizeFilter = (filter) =>
  [filter?.settings?.name, filter?.name]
    .filter((value) => typeof value === 'string' && value.length > 0)
    .some((value) => HR_SIZE_FILTER_PARAMS.has(value.toLowerCase()));

/**
 * Return the filter with its values ordered for display. Hello Retail returns sizes in its own
 * (count-based) order, which mixes ring sizes, MM, CM and S/M/L/XL together.
 */
const sortHelloRetailFilterValues = (filter) => {
  if (!isHelloRetailSizeFilter(filter) || !Array.isArray(filter?.values)) return filter;

  const sortedValues = filter.values.slice().sort((a, b) => {
    const [groupA, valueA] = helloRetailSizeSortKey(a?.title);
    const [groupB, valueB] = helloRetailSizeSortKey(b?.title);

    if (groupA !== groupB) return groupA - groupB;
    if (valueA !== valueB) return valueA - valueB;

    // Unparsed labels keep a stable, readable order instead of Hello Retail's.
    return String(a?.title ?? '').localeCompare(String(b?.title ?? ''), undefined, { numeric: true });
  });

  return { ...filter, values: sortedValues };
};

const createPriceRangeFilter = (filterOject, instanceSuffix = '', isCollapsed = false) => {
  const rawMinValue = Number(filterOject?.min);
  const minValue = Number.isFinite(rawMinValue) ? Math.floor(rawMinValue) : 0;
  const rawMaxValue = Number(filterOject?.max);
  const maxValue = Number.isFinite(rawMaxValue) ? rawMaxValue : 0;
  const rangeMax = Math.max(Math.ceil(maxValue), minValue + 1);
  const searchParams = new URLSearchParams(window.location.search);
  const fallbackSelectedMin = Number(filterOject?.selectedMin);
  const fallbackSelectedMax = Number(filterOject?.selectedMax);
  const hasUrlMin = searchParams.has('filter.v.price.gte') && searchParams.get('filter.v.price.gte') !== '';
  const hasUrlMax = searchParams.has('filter.v.price.lte') && searchParams.get('filter.v.price.lte') !== '';
  const urlSelectedMin = hasUrlMin ? Number(searchParams.get('filter.v.price.gte')) : NaN;
  const urlSelectedMax = hasUrlMax ? Number(searchParams.get('filter.v.price.lte')) : NaN;
  const baseSelectedMin = Number.isFinite(fallbackSelectedMin) ? fallbackSelectedMin : minValue;
  const baseSelectedMax = Number.isFinite(fallbackSelectedMax) ? fallbackSelectedMax : rangeMax;
  const rawSelectedMin = Number.isFinite(urlSelectedMin) ? urlSelectedMin : baseSelectedMin;
  const rawSelectedMax = Number.isFinite(urlSelectedMax) ? urlSelectedMax : baseSelectedMax;
  const selectedMinValue = Math.max(minValue, Math.min(Math.floor(rawSelectedMin), rangeMax - 1));
  const selectedMaxValue = Math.min(rangeMax, Math.max(Math.ceil(rawSelectedMax), selectedMinValue + 1));
  const handle = filterOject?.name
  const title = filterOject?.settings?.title
  const lowerBoundProgress = rangeMax > 0 ? (selectedMinValue / rangeMax * 100) : 0
  const higherBoundProgress = rangeMax > 0 ? (selectedMaxValue / rangeMax * 100) : 100
  const stepValue = 1
  const idPre = filterInstanceIdPrefix(instanceSuffix)
  const { className, openAttr } = helloRetailDetailsOpenAttrs(isCollapsed)

  return `
    <details class="${className}" is="accordion-details" data-filter data-index="${handle}"${openAttr}>
      <summary class="details__summary flex items-center justify-between gap-2 cursor-pointer">
        <span class="text-base-xl font-medium">${title}</span>

        <svg class="icon icon-chevron-up icon-md" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 15L12 9L18 15"></path>
        </svg>
      </summary>

      <div class="details__content">
        <price-range class="price-range grid gap-8">
          <label class="sr-only" for="${idPre}FacetFormRangeFromPrice-${handle}">${window.translate.collections.filtering.from_price}</label>
          <label class="sr-only" for="${idPre}FacetFormRangeToPrice-${handle}">${window.translate.collections.filtering.to_price}</label>
          <div class="range-wrapper relative" style="--range-min: ${lowerBoundProgress}%;--range-max: ${higherBoundProgress}%;">
            <input class="range w-full pointer-events-none" type="range"
              min="0"
              max="${rangeMax}"
              step="${stepValue}"
              value="${selectedMinValue}"
              id="${idPre}FacetFormRangeFromPrice-${handle}"
            />
            <input class="range w-full pointer-events-none absolute top-0 left-0" type="range"
              min="0"
              max="${rangeMax}"
              step="${stepValue}"
              value="${selectedMaxValue}"
              id="${idPre}FacetFormRangeToPrice-${handle}"
            />
          </div>
          <div class="input-wrapper flex items-center gap-6">
            <div class="field flex items-center grow relative">
              <label class="sr-only" for="${idPre}FacetFormInputFromPrice-${handle}">${window.translate.collections.filtering.from_price}</label>
              <span class="prefix absolute left-0">${window.helloRetail.currencySymbol}</span>
              <input class="input" type="number" inputmode="numeric"
                name="filter.v.price.gte"
                id="${idPre}FacetFormInputFromPrice-${handle}"
                autocomplete="off"
                placeholder="0"
                min="0"
                max="${rangeMax}"
                step="${stepValue}"
                data-default-value="${minValue}"
                value="${selectedMinValue}"
              />
            </div>
            <span>${window.translate.collections.filtering.to}</span>
            <div class="field flex items-center grow relative">
              <label class="sr-only" for="${idPre}FacetFormInputToPrice-${handle}">${window.translate.collections.filtering.to_price}</label>
              <span class="prefix absolute left-0">${window.helloRetail.currencySymbol}</span>
              <input class="input" type="number" inputmode="numeric"
                name="filter.v.price.lte"
                id="${idPre}FacetFormInputToPrice-${handle}"
                autocomplete="off"
                placeholder="${rangeMax}"
                min="${minValue}"
                max="${rangeMax}"
                step="${stepValue}"
                data-default-value="${rangeMax}"
                value="${selectedMaxValue}"
              />
            </div>
          </div>
        </price-range>
      </div>
    </details>
  `;
};

const createBooleanFilter = (filterOject, instanceSuffix = '') => {
  const handle = filterOject?.name ?? '';
  const title = filterOject?.settings?.title ?? '';
  const paramName = filterOject?.settings?.name ?? handle;
  const rawQueryValue = listCheckboxValueFromQuery(filterOject?.values?.[0]?.query, handle);
  const value = rawQueryValue || 'true';
  const isSelected = (filterOject?.values || []).some((v) => v?.selected === true);
  const idPre = filterInstanceIdPrefix(instanceSuffix);
  const inputId = `${idPre}${String(handle).replace(/[^\w.-]/g, '_')}`;

  return `
    <div class="details" data-filter data-index="${handle}">
      <div class="details__summary flex items-center justify-between gap-2">
        <label for="${escapeHtml(inputId)}" class="text-base-xl font-medium cursor-pointer">
          ${escapeHtml(title)}
        </label>
        <input name="${escapeHtml(paramName)}" class="switch" id="${escapeHtml(inputId)}" type="checkbox" value="${escapeHtml(value)}" ${isSelected ? 'checked' : ''} />
      </div>
    </div>
  `;
};

const createListFilter = (filterOject, instanceSuffix = '', isCollapsed = false) => {
  const handle = filterOject?.name ?? '';
  const paramName = filterOject?.settings?.name ?? handle;
  const title = filterOject?.settings?.title ?? '';
  const values = filterOject?.values ?? [];
  const idPre = filterInstanceIdPrefix(instanceSuffix);
  const { className, openAttr } = helloRetailDetailsOpenAttrs(isCollapsed);

  const rows = values
    .map((value, index) => {
      const checkboxValue = listCheckboxValueFromQuery(value?.query, handle);
      const inputId = `${idPre}FacetList-${String(handle).replace(/[^\w.-]/g, '_')}-${index}`;
      const label = value?.title ?? checkboxValue;
      const count = Number(value?.count ?? 0);
      const isSelected = value?.selected === true;
      const isDisabled = count === 0;

      return `
        <li class="flex items-center gap-3">
          <input
            name="${escapeHtml(paramName)}"
            value="${escapeHtml(checkboxValue)}"
            class="checkbox"
            id="${escapeHtml(inputId)}"
            type="checkbox"
            ${isSelected ? 'checked' : ''}
            ${isDisabled ? 'disabled' : ''}
          />
          <label for="${escapeHtml(inputId)}" class="flex items-baseline gap-1 cursor-pointer">
            <span>${escapeHtml(label)}</span>
            <span class="text-sm${isSelected ? '' : ' text-opacity'}">(${count})</span>
          </label>
        </li>`;
    })
    .join('');

  return `
    <details class="${className}" is="accordion-details" data-filter data-index="${escapeHtml(handle)}"${openAttr}>
      <summary class="details__summary flex items-center justify-between gap-2 cursor-pointer">
        <span class="text-base-xl font-medium">${escapeHtml(title)}</span>
        <svg class="icon icon-chevron-up icon-md" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 15L12 9L18 15"></path>
        </svg>
      </summary>
      <div class="details__content">
        <ul class="list grid gap-3" role="list">
          ${rows}
        </ul>
      </div>
    </details>
  `;
};

const createColorFilter = (filterOject, instanceSuffix = '', isCollapsed = false) => {
  const handle = filterOject?.name ?? '';
  const paramName = filterOject?.settings?.name ?? handle;
  const title = filterOject?.settings?.title ?? '';
  const values = filterOject?.values ?? [];
  const idPre = filterInstanceIdPrefix(instanceSuffix);
  const { className, openAttr } = helloRetailDetailsOpenAttrs(isCollapsed);
  const colorFilterData = document.querySelector('#colorFilterData');
  let colorData = null
  
  if (colorFilterData) {
    colorData = JSON.parse(colorFilterData.textContent);  
  }

  const rows = values
    .map((value, index) => {
      const checkboxValue = listCheckboxValueFromQuery(value?.query, handle);
      const inputId = `${idPre}FacetList-${String(handle).replace(/[^\w.-]/g, '_')}-${index}`;
      const label = value?.title ?? checkboxValue;
      const count = Number(value?.count ?? 0);
      const isSelected = value?.selected === true;
      const isDisabled = count === 0;
      const matchingColor = colorData?.colors?.find(color => color?.label?.toLowerCase() === value?.title?.toLowerCase());
      
      let swatch_image = null;
      let swatch_color = null;

      if (matchingColor) {
        swatch_image = matchingColor?.image;

        if (!matchingColor?.image) {
          swatch_color = matchingColor?.color;
        }
      }

      if (swatch_color === null && swatch_image === null) {
        swatch_color = escapeHtml(label)
      }

      if (swatch_color === null && swatch_image === null) {
        swatch_color = escapeHtml(label)
      }

      return `
        <li>
          <magnet-element class="block">
            <input
              name="${escapeHtml(paramName)}"
              value="${escapeHtml(checkboxValue)}"
              class="checkbox"
              id="${escapeHtml(inputId)}"
              type="checkbox"
              ${isSelected ? 'checked' : ''}
              ${isDisabled ? 'disabled' : ''}
            />
            <label 
              data-value="${escapeHtml(label)}" 
              class="swatch ${swatch_image ? 'with-image' : ''} block relative ${isSelected ? 'selected' : ''}"
              for="${escapeHtml(inputId)}"
              style="--swatch-background: ${swatch_color ? swatch_color : 'transparent'};${swatch_image ? `--swatch-background-image: url(${swatch_image});` : ''}">
                <span class="text-xs text-left pointer-events-none">${escapeHtml(label)} (${count})</span>
            </label>
          </magnet-element>
        </li>`;
    })
    .join('');

  return `
    <details class="${className}" is="accordion-details" data-filter data-index="${escapeHtml(handle)}"${openAttr}>
      <summary class="details__summary flex items-center justify-between gap-2 cursor-pointer">
        <span class="text-base-xl font-medium">${escapeHtml(title)}</span>
        <svg class="icon icon-chevron-up icon-md" viewBox="0 0 24 24" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 15L12 9L18 15"></path>
        </svg>
      </summary>
      <div class="details__content">
        <ul class="swatches swatches--${window.productCardInfo.rounded_swatch} flex items-center flex-wrap gap-4" role="list">
          ${rows}
        </ul>
      </div>
    </details>
  `;
};

const buildHelloRetailSortOptions = (sortData, preferredSortValue) => {
  const preferred =
    typeof preferredSortValue === 'string' && preferredSortValue.length > 0
      ? preferredSortValue
      : null;
  const featuredOption = {
    name: window.translate.collections.sorting.featured,
    value: HR_FEATURED_SORT_VALUE,
  };
  const apiSortOptions = sortData?.flatMap(option => ([
    {
      name: option?.settings?.ascendingText,
      value: option?.ascending?.query
    },
    {
      name: option?.settings?.descendingText,
      value: option?.descending?.query
    }
  ])) ?? [];
  const sortOptions = [featuredOption, ...apiSortOptions];

  // Derived from the sorter we actually sent (the URL), not from HR's `selected`
  // flag: that flag only mirrors our own request, and trusting it would make
  // "featured" bounce back to the previous sorter as soon as we clear `sort_by`.
  // No sorter in the URL, or one HR no longer offers, means featured.
  const activeSortOption =
    (preferred != null ? sortOptions.find((option) => option?.value === preferred) : undefined) ??
    featuredOption;

  return { sortOptions, activeSortOption };
};

const createSortElement = (sortData, preferredSortValue) => {
  const { sortOptions, activeSortOption } = buildHelloRetailSortOptions(sortData, preferredSortValue);

  const createSortOptions = (sortOptions) => {
    return sortOptions.map(sortOption => {
      const checked = sortOption?.value === activeSortOption?.value ? ' checked' : '';
      return `
        <li class="opacity-0 relative">
          <input id="SortBy-${sortOption?.value}" class="sr-only" name="sort_by" type="radio" value="${sortOption?.value}"${checked}>
          <label for="SortBy-${sortOption?.value}" class="reversed-link text-xl">${sortOption?.name}</label>
        </li>
      `;
    }).join('');
  };

  const sortOptionsHtml = createSortOptions(sortOptions);

  return `
    <facet-sort class="sort-wrapper relative">
      <hover-element class="button button--secondary sort-by whitespace-nowrap overflow-hidden z-3">
        <span class="btn-fill" data-fill=""></span>
        <div class="btn-text">
          <span class="sort-header flex items-center justify-center gap-5 relative w-full">
            <span class="sort-selection">${activeSortOption?.name}</span>
            <span class="sort-title text-sm uppercase absolute left-0 opacity-0 tracking-widest">${activeSortOption?.name}</span>
            <span class="sort-close flex items-center justify-center rounded-full">
              <svg class="icon icon-close icon-sm" viewBox="0 0 20 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation">
                <path stroke-linecap="round" stroke-linejoin="round" d="M5 15L15 5M5 5L15 15"></path>
              </svg>
            </span>
          </span>
          <ul class="sort-listbox text-left grid gap-2 absolute left-0 opacity-0 invisible w-full h-auto">
            ${sortOptionsHtml}
          </ul>
        </div>
      </hover-element>
    </facet-sort>
  `;
};

const createSortElementMobile = (sortData, preferredSortValue) => {
  const { sortOptions, activeSortOption } = buildHelloRetailSortOptions(sortData, preferredSortValue);

  const allSortOptionsHtml = sortOptions.map(option => `
    <option value="${option?.value}"${option?.value === activeSortOption?.value ? ' selected' : ''}>${option?.name}</option>
  `).join('');

  return `
    ${allSortOptionsHtml}
  `
}

const createPaginationElement = (paginationData, pageSize) => {
  const start = Number(paginationData?.start ?? 0);
  const count = Math.max(1, pageSize || Number(paginationData?.count ?? 1));
  const total = Math.max(0, Number(paginationData?.total ?? 0));
  const totalPages = Math.max(1, Math.ceil(total / count));
  const currentPage = Math.min(totalPages, Math.floor(start / count) + 1);

  const buildPageUrl = (pageNumber) => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(pageNumber));
    return `${url.pathname}${url.search}`;
  };

  const getVisiblePages = () => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);

    if (currentPage <= 3) {
      pages.add(2);
      pages.add(3);
      pages.add(4);
    }

    if (currentPage >= totalPages - 2) {
      pages.add(totalPages - 1);
      pages.add(totalPages - 2);
      pages.add(totalPages - 3);
    }

    return Array.from(pages)
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);
  };

  const renderPageItems = () => {
    const visiblePages = getVisiblePages();
    let previousPage = null;

    return visiblePages.map((page) => {
      const needsEllipsis = previousPage !== null && page - previousPage > 1;
      previousPage = page;

      const ellipsisHtml = needsEllipsis
        ? `<span class="page flex items-center justify-center rounded-full" aria-label="Pagina &amp;hellip;">…</span>`
        : '';

      if (page === currentPage) {
        return `${ellipsisHtml}<span class="page current flex items-center justify-center rounded-full pointer-events-none" aria-current="page">${page}</span>`;
      }

      return `${ellipsisHtml}<a class="page flex items-center justify-center rounded-full" href="${buildPageUrl(page)}" is="magnet-link" aria-label="Pagina ${page}"><span class="btn-text" data-text="">${page}</span></a>`;
    }).join('');
  };

  const showPrevious = currentPage > 1;
  const showNext = currentPage < totalPages;
  const previousPageUrl = buildPageUrl(currentPage - 1);
  const nextPageUrl = buildPageUrl(currentPage + 1);
  const pageItemsHtml = renderPageItems();

  return `
    <div class="pagination flex flex-wrap justify-center align-self-center md:gap-2 text-sm md:text-base">
      ${showPrevious ? `
      <a
        class="previous flex items-center justify-center gap-2 font-medium"
        href="${previousPageUrl}"
        is="magnet-link"
        aria-label="Vorig"
        data-href="${previousPageUrl}"
      >
        <svg class="icon icon-arrow-left icon-sm transform stroke-1 shrink-0"
          viewBox="0 0 21 20" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation">
          <path stroke-linecap="round" stroke-linejoin="round" d="M17.996 10h-15M2.996 10l5.833 5.833M2.996 10l5.833-5.833">
          </path>
        </svg>
        <span class="btn-text" data-text="">${window.translate.general.pagination.previous}</span>
      </a>` : ''}

      ${pageItemsHtml}

      ${showNext ? `
      <a
        class="next flex items-center justify-center gap-2 font-medium"
        href="${nextPageUrl}"
        is="magnet-link"
        aria-label="Volgende"
      >
        <span class="btn-text" data-text="">${window.translate.general.pagination.next}</span>
        <svg class="icon icon-arrow-right icon-sm transform stroke-1 shrink-0" viewBox="0 0 21 20" stroke="currentColor"
          fill="none" xmlns="http://www.w3.org/2000/svg" role="presentation">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 10H18M18 10L12.1667 4.16675M18 10L12.1667 15.8334">
          </path>
        </svg>
      </a>` : ''}
    </div>
  `;
};